import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { adminApi, invalidateAdminClientCache } from '../lib/api';
import ProductImageUploadModal from '../components/ProductImageUploadModal';
import ReceiptsTab from '../components/ReceiptsTab';
import SpecialsTab from '../components/SpecialsTab';
import RequestsTab from '../components/RequestsTab';
import CustomerSynopsis from '../components/CustomerSynopsis';
import PricingTab from '../components/PricingTab';
import DeliverySettingsEditor from '../components/DeliverySettingsEditor';
import CustomerDetailsEditor from '../components/CustomerDetailsEditor';
import AdminOrderEntry from '../components/AdminOrderEntry';
import AllSitesLink from '../components/AllSitesLink';
import StaffSignIn from '../components/StaffSignIn';
import useStaffSession from '../hooks/useStaffSession';
import { siteUrl, siteByKey } from '../lib/sites';
import { getStaffToken, setStaffToken } from '../lib/staffSession';

const API = process.env.REACT_APP_API_URL;

export default function Admin() {
  // The staff sign-in every staff site shares (hooks/useStaffSession.js).
  const session = useStaffSession('manage');
  const { token } = session;
  const [me, setMe] = useState(null);
  const [meError, setMeError] = useState('');
  // ?tab= lets an external round trip (QuickBooks OAuth) land on the screen
  // the admin started from instead of the default.
  const requestedTab = new URLSearchParams(window.location.search).get('tab');
  const [tab, setTab] = useState(requestedTab || 'catalog');

  // Confirm the sign-in still works by fetching /admin/me. A 401 sends the
  // browser to sign in again (api.js); anything else is shown.
  useEffect(() => {
    if (!token) return;
    // Never serve a previous sign-in's cached responses to a new identity.
    invalidateAdminClientCache();
    setMeError('');
    adminApi.me()
      .then(setMe)
      .catch((e) => { if (e.message !== 'Unauthorized') setMeError(e.message); });
  }, [token]);

  // Just signed in: prewarm the heavy SOS-backed endpoints so clicking
  // between tabs feels instant. Fire-and-forget — errors are swallowed since
  // these are speculative fetches. Each response gets cached client-side.
  // Only prewarm what this role's tabs can actually show.
  const prewarmed = useRef(false);
  useEffect(() => {
    if (!session.fresh || !me || prewarmed.current) return;
    prewarmed.current = true;
    const tabs = ROLE_TABS[me.role] || [];
    if (tabs.includes('catalog')) adminApi.getCatalog().catch(() => {});
    if (tabs.includes('customers')) adminApi.getCustomerActivity({ days: 90 }).catch(() => {});
  }, [session.fresh, me]);

  // A 403 means the page is stale about who's signed in: the sign-in was
  // switched in another tab, or an owner changed this person's role
  // mid-session. Re-fetch /admin/me and let the tab set re-render for the
  // real role instead of showing "Forbidden". (A token another tab rewrites
  // reaches `token` through useStaffSession, which re-runs the fetch above.)
  const refreshingMe = useRef(false);
  useEffect(() => {
    async function refreshMe() {
      if (refreshingMe.current) return;
      refreshingMe.current = true;
      try {
        setMe(await adminApi.me({ noCache: true }));
      } catch { /* a 401 in this fetch already went to sign in */ }
      finally { refreshingMe.current = false; }
    }
    window.addEventListener('ss-admin-forbidden', refreshMe);
    return () => window.removeEventListener('ss-admin-forbidden', refreshMe);
  }, []);

  function signOut() {
    invalidateAdminClientCache();
    session.signOut();
  }

  // The capture app is a separate surface with its own token key. Hand it the
  // session we already have so crossing over doesn't mean a second sign-in.
  function openCapture() {
    const current = getStaffToken('manage');
    if (current) setStaffToken('receipts', current);
    window.location.hash = '#receipt-capture';
  }

  const myTabs = ROLE_TABS[me?.role] || [];
  const canEdit = tabKey => (TAB_EDIT_ROLES[tabKey] || []).includes(me?.role);

  // Groups keep the top row to a handful of buttons instead of 14 tabs. A
  // group with nothing this role can see never renders.
  const myGroups = TAB_GROUPS
    .map(([key, label, keys]) => [key, label, keys.filter(k => myTabs.includes(k))])
    .filter(([, , keys]) => keys.length);
  const orderedTabs = myGroups.flatMap(([, , keys]) => keys);
  const openGroup = myGroups.find(([, , keys]) => keys.includes(tab));

  // Land on the first tab this role can see — the default ('catalog')
  // isn't available to every role.
  useEffect(() => {
    if (me && orderedTabs.length && !orderedTabs.includes(tab)) setTab(orderedTabs[0]);
  }, [me, orderedTabs, tab]);

  if (!token) return <StaffSignIn session={session} siteName="Manage" />;

  const initials = (me?.name || me?.email || 'A')
    .split(/[\s@.]+/).filter(Boolean).slice(0, 2).map(p => p[0]?.toUpperCase()).join('');

  return (
    <div style={s.page}>
      <div style={s.container}>
        <div style={s.topBar}>
          <div>
            <h1 style={s.pageTitle}>Silver Service Admin</h1>
            <p style={s.sub}>Order analytics, customer management & exports</p>
          </div>
          <div style={s.topBarRight}>
            {me && (
              <div style={s.userChip} title={me.email || ''}>
                <div style={s.avatar}>{initials}</div>
                <div style={s.userChipText}>
                  <div style={s.userChipName}>{me.name || me.email}</div>
                  <div style={s.userChipRole}>{ROLE_LABELS[me.role] || 'Admin'}</div>
                </div>
              </div>
            )}
            {CAPTURE_ROLES.includes(me?.role) && (
              <button style={s.btnOutline} onClick={openCapture} title="Photograph an AMEX receipt">
                Receipt Capture
              </button>
            )}
            <AllSitesLink style={s.btnOutline} />
            <button style={s.btnOutline} onClick={signOut} title="Signs this browser out of every staff site">Sign Out</button>
          </div>
        </div>

        <div style={s.groupBar}>
          {myGroups.map(([key, label, keys]) => (
            <button
              key={key}
              style={openGroup?.[0] === key ? { ...s.groupBtn, ...s.groupBtnActive } : s.groupBtn}
              onClick={() => setTab(keys[0])}
            >
              {label}
            </button>
          ))}
        </div>

        <MovedNotice role={me?.role} />

        {openGroup && openGroup[2].length > 1 && (
          <div style={s.tabs}>
            {openGroup[2].map(key => (
              <button
                key={key}
                style={tab === key ? { ...s.tab, ...s.tabActive } : s.tab}
                onClick={() => setTab(key)}
              >
                {TAB_LABELS[key]}
              </button>
            ))}
          </div>
        )}

        {!me && <p style={s.sub}>{meError || 'Loading…'}</p>}
        {me && myTabs.includes(tab) && (
          <>
            {tab === 'orders' && <OrdersTab />}
            {tab === 'portal-orders' && <OrdersTab source="portal" />}
            {tab === 'customers' && <CustomersTab readOnly={!canEdit('customers')} me={me} />}
            {tab === 'new-customers' && <NewCustomersTab readOnly={!canEdit('new-customers')} canInvite={canEdit('new-customer-invites')} />}
            {tab === 'requests' && <RequestsTab readOnly={!canEdit('requests')} />}
            {tab === 'catalog' && <CatalogTab readOnly={!canEdit('catalog')} />}
            {tab === 'specials' && <SpecialsTab readOnly={!canEdit('specials')} />}
            {tab === 'representatives' && <RepresentativesTab canManage={canEdit('representatives')} myEmail={me.email} />}
            {tab === 'receipts' && <ReceiptsTab />}
            {tab === 'activity' && <ActivityTab />}
            {tab === 'crosswalk' && <CrosswalkTab />}
            {tab === 'pricing' && <PricingTab readOnly={!canEdit('pricing')} />}
            {tab === 'export' && <ExportTab />}
            {tab === 'settings' && <SettingsTab />}
          </>
        )}
      </div>
    </div>
  );
}

// ── Helpers ──
function getLast30() {
  const end = new Date().toISOString().split('T')[0];
  const start = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];
  return { start, end };
}
function getDateRange(range) {
  const end = new Date().toISOString().split('T')[0];
  if (range === '7d') return { start: new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0], end };
  if (range === '30d') return { start: new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0], end };
  if (range === '90d') return { start: new Date(Date.now() - 90 * 86400000).toISOString().split('T')[0], end };
  if (range === 'ytd') return { start: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0], end };
  return { start: '', end };
}
function fmt$(v) { return '$' + (v || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }

// Role-based tab visibility and edit rights. Mirrors ROLE_ACCESS in
// backend lib/staff-roles.js — keep both in sync. Backend is the source of
// truth for access control (returns 403); these only control what renders.
// `me.role` comes from /admin/me, which resolves the live AdminUsers record,
// so a role change applies on the next page load without re-login.
// [groupKey, groupLabel, [tabKey, ...]] — the group bar is the top row and
// the tabs below it belong to whichever group is open. Order here is the
// order shown; ROLE_TABS still decides what a role may see.
// Sales work (win-back, account health, the AI summary) lives on the sales
// site and company reporting on the owners-only reports site — see
// docs/site-map.md. Orders move to the fulfillment site once it exists.
const TAB_GROUPS = [
  ['customers', 'Customers', ['customers', 'new-customers', 'requests']],
  ['orders', 'Orders', ['orders', 'portal-orders']],
  ['products', 'Catalog & pricing', ['catalog', 'pricing', 'specials', 'crosswalk']],
  ['back-office', 'Back office', ['representatives', 'receipts', 'activity', 'export', 'settings']],
];
const TAB_LABELS = {
  catalog: 'Catalog',
  specials: 'Specials',
  customers: 'Customers',
  'new-customers': 'New Customers',
  requests: 'Requests',
  representatives: 'Staff',
  orders: 'Orders',
  'portal-orders': 'Portal Orders',
  receipts: 'Receipts',
  activity: 'Activity',
  crosswalk: 'Crosswalk',
  pricing: 'Tier Pricing',
  export: 'Export',
  settings: 'Settings',
};
const ROLE_TABS = {
  owner: ['catalog', 'pricing', 'specials', 'customers', 'new-customers', 'requests', 'representatives', 'orders', 'portal-orders', 'receipts', 'activity', 'crosswalk', 'export', 'settings'],
  silver_service_admin: ['catalog', 'pricing', 'specials', 'customers', 'new-customers', 'requests', 'representatives', 'orders', 'portal-orders', 'receipts', 'activity', 'crosswalk', 'export', 'settings'],
  sales_rep: ['orders', 'portal-orders', 'customers', 'new-customers', 'specials', 'catalog', 'crosswalk', 'requests'],
  technician: ['orders', 'portal-orders', 'customers', 'requests'],
  delivery: ['orders', 'portal-orders', 'customers'],
};
// Roles that may use the receipt-capture app, so the portal can offer a way
// in. receipt_capturer is absent because that role can't reach this portal at
// all. Mirrors the /receipts/* guard in backend/lambda/index.js.
const CAPTURE_ROLES = ['owner', 'silver_service_admin', 'sales_rep', 'technician', 'delivery'];

// Which roles may edit within a tab; everyone else gets the view-only
// rendering. Tabs not listed here are edit-free or owner/admin-only anyway.
const TAB_EDIT_ROLES = {
  catalog: ['owner', 'silver_service_admin'],
  specials: ['owner', 'silver_service_admin'],
  pricing: ['owner', 'silver_service_admin'],
  customers: ['owner', 'silver_service_admin', 'sales_rep'],
  'new-customers': ['owner', 'silver_service_admin'],
  // Sales reps send the application link but don't approve applications.
  'new-customer-invites': ['owner', 'silver_service_admin', 'sales_rep'],
  // Technicians work the service requests, so they may move them along.
  requests: ['owner', 'silver_service_admin', 'technician'],
  representatives: ['owner'],
};
// What each role opens, shown when an owner picks one for someone new.
const ROLE_OPENS = {
  owner: 'Opens everything, including Reports and adding staff.',
  silver_service_admin: 'Opens Manage, the service desk, Fulfillment and Receipts, plus their own accounts in Sales.',
  sales_rep: 'Opens their own accounts in Sales, the service desk, Fulfillment (to look), Manage (customers and orders) and Receipts.',
  technician: 'Opens the service desk and Receipts, and can look up customers in Manage.',
  delivery: 'Opens Fulfillment for deliveries and Receipts, and can look up customers in Manage.',
  receipt_capturer: 'Opens only the receipt app, for photographing company-card receipts.',
};
const ROLE_LABELS = {
  owner: 'Owner',
  silver_service_admin: 'Admin',
  sales_rep: 'Sales',
  technician: 'Technician',
  delivery: 'Delivery',
  receipt_capturer: 'Receipt Capturer',
};
function fmtDate(d) { if (!d) return '—'; return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }

// ── Orders Tab — Sales Order dashboard ──
// `source="portal"` restricts to orders placed through the customer portal.
// Backend returns the full date-range set + summary; client handles filtering,
// sort, pagination in-memory so toggling filters is instant.
const PAST_DUE_DAYS = 7;
const PAGE_SIZE = 25;
const SOS_LIST_URL = 'https://live.sosinventory.com/SalesOrder';

function OrdersTab({ source }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [range, setRange] = useState('30d');
  const [statusFilter, setStatusFilter] = useState('all'); // all | open | partial | closed | pastdue
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(null);
  const [copyToast, setCopyToast] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { start, end } = getDateRange(range);
      const params = { startdate: start, enddate: end };
      if (source) params.source = source;
      const result = await adminApi.getOrders(params);
      setData(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [range, source]);

  useEffect(() => { load(); }, [load]);
  // Reset page when the filter set changes; collapse expansion only on filter
  // changes that re-order or remove rows (not on search keystrokes — that's
  // annoying mid-search).
  useEffect(() => { setPage(1); setExpanded(null); }, [range, statusFilter, source]);
  useEffect(() => { setPage(1); }, [search]);

  function copyOrderNumber(e, number) {
    e.stopPropagation();
    // navigator.clipboard is undefined on insecure (http) contexts and older
    // browsers. Fall back to the legacy execCommand path; if that also fails,
    // show the number in a toast so the user can manually select-copy.
    const showToast = (msg) => {
      setCopyToast(msg);
      setTimeout(() => setCopyToast(''), 2500);
    };
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(number)
        .then(() => showToast(number))
        .catch(() => showToast(`Copy failed — order #: ${number}`));
      return;
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = number;
      ta.style.position = 'fixed';
      ta.style.top = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      showToast(ok ? number : `Copy failed — order #: ${number}`);
    } catch {
      showToast(`Copy unavailable — order #: ${number}`);
    }
  }

  const allOrders = data?.orders || [];
  const summary = data?.summary || {};

  // Filter + sort. Default sort: open-like oldest first, then by date desc.
  const filtered = allOrders.filter(o => {
    if (statusFilter === 'open' && (o.status || '').toLowerCase() !== 'open') return false;
    if (statusFilter === 'partial' && (o.status || '').toLowerCase() !== 'partial') return false;
    if (statusFilter === 'closed' && (o.status || '').toLowerCase() !== 'closed') return false;
    if (statusFilter === 'pastdue') {
      const s = (o.status || '').toLowerCase();
      if (s !== 'open' && s !== 'partial') return false;
      if ((o.ageDays || 0) < PAST_DUE_DAYS) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      if (!(o.number || '').toLowerCase().includes(q) &&
          !(o.customerName || '').toLowerCase().includes(q) &&
          !(o.customerPO || '').toLowerCase().includes(q)) return false;
    }
    return true;
  }).sort((a, b) => {
    const aOpen = (a.status || '').toLowerCase() === 'open' || (a.status || '').toLowerCase() === 'partial';
    const bOpen = (b.status || '').toLowerCase() === 'open' || (b.status || '').toLowerCase() === 'partial';
    if (aOpen && bOpen) return (b.ageDays || 0) - (a.ageDays || 0); // oldest open first
    if (aOpen) return -1;
    if (bOpen) return 1;
    return (b.date || '').localeCompare(a.date || ''); // newest closed first
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageStart = (page - 1) * PAGE_SIZE;
  const visible = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <div>
      {source === 'portal' && (
        <p style={{ ...s.sub, marginTop: 0 }}>
          Showing only orders placed through the customer ordering portal.
        </p>
      )}

      {/* Top action bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div style={s.rangeBar}>
          {['7d', '30d', '90d', 'ytd'].map(r => (
            <button key={r} style={range === r ? { ...s.rangeBtn, ...s.rangeBtnActive } : s.rangeBtn} onClick={() => setRange(r)}>
              {r === 'ytd' ? 'YTD' : r}
            </button>
          ))}
        </div>
        <a href={SOS_LIST_URL} target="_blank" rel="noopener noreferrer" style={s.sosLinkBtn}>
          Open in SOS ↗
        </a>
      </div>

      {loading && <p style={s.sub}>Loading orders...</p>}
      {error && <p style={s.errorText}>{error}</p>}

      {!loading && data && (
        <>
          {/* Summary KPIs */}
          <div style={s.kpiGrid}>
            <div style={s.kpiCard}>
              <div style={s.kpiLabel}>Open</div>
              <div style={{ ...s.kpiValue, color: '#e65100' }}>{summary.totalOpenLike || 0}</div>
              <div style={{ ...s.kpiLabel, marginTop: 4, marginBottom: 0 }}>
                {summary.openCount || 0} open · {summary.partialCount || 0} partial
              </div>
            </div>
            <div style={s.kpiCard}>
              <div style={s.kpiLabel}>$ Open</div>
              <div style={{ ...s.kpiValue, color: '#e65100' }}>{fmt$(summary.dollarsOpen || 0)}</div>
            </div>
            <div style={s.kpiCard}>
              <div style={s.kpiLabel}>Closed (in range)</div>
              <div style={{ ...s.kpiValue, color: '#2e7d32' }}>{summary.closedCount || 0}</div>
            </div>
            <div style={s.kpiCard}>
              <div style={s.kpiLabel}>Unique Customers</div>
              <div style={s.kpiValue}>{summary.uniqueCustomers || 0}</div>
            </div>
            <div style={s.kpiCard}>
              <div style={s.kpiLabel}>Oldest Open</div>
              <div style={{ ...s.kpiValue, color: (summary.oldestOpenAgeDays || 0) >= PAST_DUE_DAYS ? '#c62828' : '#333' }}>
                {summary.oldestOpenAgeDays || 0}d
              </div>
              {summary.oldestOpenNumber && (
                <div style={{ ...s.kpiLabel, marginTop: 4, marginBottom: 0 }}>{summary.oldestOpenNumber}</div>
              )}
            </div>
          </div>

          {data.truncated && (
            <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, padding: '0.5rem 0.75rem', marginBottom: '0.75rem', fontSize: '0.82rem', color: '#664d03' }}>
              Showing {allOrders.length} orders. Your date range goes back further than we fetched — older orders within the range aren't included, so Open / Closed / $ counts cover only recent orders. (Unique Customers is accurate for the full range.) Narrow the range to see every order.
            </div>
          )}
          {data.warnings && data.warnings.length > 0 && (
            <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, padding: '0.5rem 0.75rem', marginBottom: '0.75rem', fontSize: '0.82rem', color: '#664d03' }}>
              <strong>Some orders couldn't be fetched from SOS:</strong>
              <ul style={{ margin: '0.3rem 0 0', paddingLeft: '1.25rem' }}>
                {data.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          {/* Status filter + search */}
          <div style={s.filterBar}>
            <input
              style={{ ...s.input, flex: 1, minWidth: 200 }}
              placeholder="Search by order #, customer, or PO…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <div style={s.rangeBar}>
              {[
                ['all', 'All'],
                ['open', `Open (${summary.openCount || 0})`],
                ['partial', `Partial (${summary.partialCount || 0})`],
                ['closed', `Closed (${summary.closedCount || 0})`],
                ['pastdue', `Past Due >${PAST_DUE_DAYS}d`],
              ].map(([key, label]) => (
                <button key={key} style={statusFilter === key ? { ...s.rangeBtn, ...s.rangeBtnActive } : s.rangeBtn} onClick={() => setStatusFilter(key)}>
                  {label}
                </button>
              ))}
            </div>
            <span style={s.totalLabel}>{filtered.length} matching</span>
          </div>

          {/* Order table */}
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Status</th>
                <th style={s.th}>Order #</th>
                <th style={s.th}>Date</th>
                <th style={s.th}>Customer</th>
                <th style={s.th}>PO</th>
                <th style={s.thR}>Age</th>
                <th style={s.thR}>Items</th>
                <th style={s.thR}>Total</th>
                <th style={s.th}></th>
              </tr>
            </thead>
            <tbody>
              {visible.map(o => (
                <OrderRow key={o.id} order={o} expanded={expanded === o.id} onToggle={() => setExpanded(expanded === o.id ? null : o.id)} onCopy={copyOrderNumber} />
              ))}
              {visible.length === 0 && <tr><td colSpan={9} style={s.td}>No orders match the current filter.</td></tr>}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div style={s.pagination}>
              <button style={s.pageBtn} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
              <span style={s.pageInfo}>Page {page} of {totalPages}</span>
              <button style={s.pageBtn} disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          )}

          {copyToast && (
            <div style={s.copyToast}>Copied <strong>{copyToast}</strong> — paste into SOS search</div>
          )}
        </>
      )}
    </div>
  );
}

function statusBadgeStyle(status) {
  const map = {
    open:    { bg: '#fff3e0', fg: '#e65100' },
    partial: { bg: '#fff3cd', fg: '#664d03' },
    closed:  { bg: '#e8f5e9', fg: '#2e7d32' },
    voided:  { bg: '#fafafa', fg: '#888' },
  };
  const s = (status || '').toLowerCase();
  const c = map[s] || { bg: '#fafafa', fg: '#666' };
  return {
    display: 'inline-block', padding: '2px 8px', borderRadius: 12,
    fontSize: '0.72rem', fontWeight: 700, background: c.bg, color: c.fg,
    textTransform: 'capitalize',
  };
}

function OrderRow({ order, expanded, onToggle, onCopy }) {
  const isOpenLike = (order.status || '').toLowerCase() === 'open' || (order.status || '').toLowerCase() === 'partial';
  const isPastDue = isOpenLike && (order.ageDays || 0) >= PAST_DUE_DAYS;
  return (
    <>
      <tr style={{ cursor: 'pointer', background: isPastDue ? '#fff8f8' : undefined }} onClick={onToggle}>
        <td style={s.td}><span style={statusBadgeStyle(order.status)}>{order.status || 'Unknown'}</span></td>
        <td style={s.td}>
          <button style={s.orderNumBtn} onClick={(e) => onCopy(e, order.number)} title="Copy order number to clipboard">
            {order.number} ⧉
          </button>
        </td>
        <td style={s.td}>{fmtDate(order.date)}</td>
        <td style={s.td}>{order.customerName}</td>
        <td style={s.td}>{order.customerPO || '—'}</td>
        <td style={{ ...s.tdR, color: isPastDue ? '#c62828' : '#666', fontWeight: isPastDue ? 600 : 400 }}>
          {isOpenLike ? (order.ageDays != null ? `${order.ageDays}d` : '—') : ''}
        </td>
        <td style={s.tdR}>{order.lines?.length || 0}</td>
        <td style={s.tdR}>{fmt$(order.total)}</td>
        <td style={s.td}>{expanded ? '▾' : '▸'}</td>
      </tr>
      {expanded && (
        <tr style={{ background: '#fafafa' }}>
          <td colSpan={9} style={{ padding: '0.75rem 1rem' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr>
                  <th style={s.th}>SKU</th>
                  <th style={s.th}>Item</th>
                  <th style={s.thR}>Ordered</th>
                  <th style={s.thR}>Shipped</th>
                  <th style={s.thR}>Unit $</th>
                  <th style={s.thR}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {order.lines?.map((l, i) => (
                  <tr key={i}>
                    <td style={s.td}>{l.sku || '—'}</td>
                    <td style={s.td}>{l.itemName || '—'}</td>
                    <td style={s.tdR}>{l.quantity || 0}</td>
                    <td style={s.tdR}>{l.quantityShipped != null ? l.quantityShipped : '—'}</td>
                    <td style={s.tdR}>{fmt$(l.unitPrice || 0)}</td>
                    <td style={s.tdR}>{fmt$(l.amount || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {order.comment && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: '#666' }}>
                <strong>Notes:</strong> {order.comment}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ── Customers Tab ──
// readOnly: technicians and delivery staff get the customer list for
// contacts and addresses, without invite or order-guide controls.
// The Customers list is served from a background-refreshed cache (see
// handleAdminCustomerActivity), so loads are instant but the numbers can be
// up to ~25 min behind — this renders that honestly ("Updated 12 min ago").
function relativeTime(iso) {
  if (!iso) return null;
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${mins % 60}m ago`;
  return new Date(iso).toLocaleString();
}

function CustomersTab({ readOnly, me }) {
  const [customers, setCustomers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [custOrders, setCustOrders] = useState({});
  const [adminEmails, setAdminEmails] = useState({});
  const [adminEmailInput, setAdminEmailInput] = useState({});
  const [adminEmailStatus, setAdminEmailStatus] = useState({});
  const [error, setError] = useState('');
  // Order Guide editing is now a dedicated view; tracking the currently
  // edited customer here lets us route from list → editor → back to list.
  const [editingCustomer, setEditingCustomer] = useState(null);
  // Customer Synopsis is a dedicated view too — same list → detail → back flow.
  const [synopsisCustomer, setSynopsisCustomer] = useState(null);
  // Staff order entry (docs/admin-order-entry.md) — owner, admin and sales
  // reps; the order-entry area is enforced server-side too.
  const [orderEntryCustomer, setOrderEntryCustomer] = useState(null);
  const canPlaceOrders = ['owner', 'silver_service_admin', 'sales_rep'].includes(me?.role);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await adminApi.getCustomerActivity({ days: 90 });
      setCustomers(result.customers || []);
      setSummary(result.summary || null);
      setGeneratedAt(result.generatedAt || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function loadCustOrders(custId) {
    if (custOrders[custId]) return;
    try {
      const result = await adminApi.getCustomerOrders(custId, { pagesize: 5 });
      setCustOrders(prev => ({ ...prev, [custId]: result.orders || [] }));
    } catch (e) {
      console.error(e);
    }
  }

  async function setAdminEmail(customerId) {
    const email = (adminEmailInput[customerId] || '').trim();
    if (!email) return;
    setAdminEmailStatus(prev => ({ ...prev, [customerId]: { loading: true } }));
    try {
      await adminApi.setAdminEmail(customerId, email);
      setAdminEmailStatus(prev => ({ ...prev, [customerId]: { success: `Admin invite sent to ${email}` } }));
      setAdminEmails(prev => ({ ...prev, [customerId]: email }));
    } catch (e) {
      setAdminEmailStatus(prev => ({ ...prev, [customerId]: { error: e.message || 'Failed' } }));
    }
  }

  // Order guide fetching / saving / catalog loading moved to OrderGuideEditor.

  const filtered = customers.filter(c => {
    if (filter !== 'all' && c.status !== filter) return false;
    if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const statusBadge = (status) => {
    const colors = { active: '#27ae60', dormant: '#e67e22', never_ordered: '#95a5a6' };
    const labels = { active: 'Active', dormant: 'Dormant', never_ordered: 'No Orders' };
    return <span style={{ ...s.badge, background: `${colors[status]}15`, color: colors[status] }}>{labels[status]}</span>;
  };

  // When editing a customer's order guide, swap the entire tab for the editor.
  if (editingCustomer) {
    return (
      <OrderGuideEditor
        customer={editingCustomer}
        onBack={() => setEditingCustomer(null)}
      />
    );
  }

  if (synopsisCustomer) {
    return (
      <CustomerSynopsis
        customer={synopsisCustomer}
        me={me}
        sections={['notes']}
        onBack={() => setSynopsisCustomer(null)}
      />
    );
  }

  if (orderEntryCustomer) {
    return (
      <AdminOrderEntry
        customer={orderEntryCustomer}
        onBack={() => setOrderEntryCustomer(null)}
      />
    );
  }

  return (
    <div>
      {/* Summary cards */}
      {summary && (
        <div style={s.kpiGrid}>
          <div style={s.kpiCard}><div style={s.kpiLabel}>Total Customers</div><div style={s.kpiValue}>{summary.total}</div></div>
          <div style={s.kpiCard}><div style={s.kpiLabel}>Active (30d)</div><div style={{ ...s.kpiValue, color: '#27ae60' }}>{summary.active}</div></div>
          <div style={s.kpiCard}><div style={s.kpiLabel}>Invited</div><div style={{ ...s.kpiValue, color: '#e67e22' }}>{summary.invited || 0}</div></div>
          <div style={s.kpiCard}><div style={s.kpiLabel}>Onboarded</div><div style={{ ...s.kpiValue, color: '#2e7d32' }}>{summary.onboarded || 0}</div></div>
        </div>
      )}

      {generatedAt && (
        <div style={{ fontSize: '0.75rem', color: '#999', textAlign: 'right', margin: '-0.75rem 0 0.5rem' }}>
          Order stats updated {relativeTime(generatedAt)} · refreshes automatically
        </div>
      )}

      {/* Search + filter */}
      <div style={s.filterBar}>
        <input style={{ ...s.input, flex: 1 }} type="text" placeholder="Search customers..." value={search} onChange={e => setSearch(e.target.value)} />
        <div style={s.rangeBar}>
          {[['all', 'All'], ['active', 'Active'], ['never_ordered', 'No Orders']].map(([key, label]) => (
            <button key={key} style={filter === key ? { ...s.rangeBtn, ...s.rangeBtnActive } : s.rangeBtn} onClick={() => setFilter(key)}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading && <p style={s.sub}>Loading customers...</p>}
      {error && <p style={s.errorText}>{error}</p>}

      {!loading && (
        <div style={s.customerList}>
          {filtered.map(c => (
            <div key={c.id} style={s.customerCard}>
              <div style={s.customerRow} onClick={() => { setExpanded(expanded === c.id ? null : c.id); if (expanded !== c.id) loadCustOrders(c.id); }}>
                <div style={s.customerInfo}>
                  <div style={s.customerName}>{c.name}</div>
                  <div style={s.customerMeta}>
                    {c.email || <span style={{ color: '#e74c3c' }}>No email</span>}
                    {c.phone ? ` · ${c.phone}` : ''}
                  </div>
                </div>
                <div style={s.customerStats}>
                  {statusBadge(c.status)}
                  {(c.portalAdmin || adminEmails[c.id]) && (
                    <span style={{
                      ...s.badge,
                      background: (c.portalAdmin?.status === 'active' || adminEmailStatus[c.id]?.success) ? '#e8f5e9' : '#fff3e0',
                      color: (c.portalAdmin?.status === 'active' || adminEmailStatus[c.id]?.success) ? '#2e7d32' : '#e65100',
                    }}>
                      {c.portalAdmin?.status === 'active' ? 'Onboarded' : 'Invited'}
                    </span>
                  )}
                  {c.portalUserCount > 0 && (
                    <span style={{ ...s.badge, background: '#e3f2fd', color: '#1565c0' }}>
                      {c.portalUserCount} {c.portalUserCount === 1 ? 'employee' : 'employees'}
                    </span>
                  )}
                  <span style={s.statText}>{c.orderCount} orders</span>
                  <span style={s.statText}>{fmt$(c.totalRevenue)}</span>
                  <span style={s.expandIcon}>{expanded === c.id ? '▾' : '▸'}</span>
                </div>
              </div>

              {expanded === c.id && (
                <div style={s.expandedContent}>
                  <div style={s.expandedRow}>
                    <div>
                      <strong>Last Order:</strong> {fmtDate(c.lastOrderDate)}
                    </div>
                  </div>

                  {/* Delivery schedule (portal-owned; Phase 1 slice A) */}
                  <CustomerDetailsEditor customer={c} readOnly={readOnly} />
                  <DeliverySettingsEditor customer={c} readOnly={readOnly} />

                  {/* Admin Email Management */}
                  <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#fafafa', borderRadius: 8, border: '1px solid #e8e8e8' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#555', marginBottom: '0.5rem' }}>Portal Admin Email</div>
                    {(c.portalAdmin || adminEmails[c.id]) && (
                      <div style={{ fontSize: '0.82rem', marginBottom: '0.5rem' }}>
                        <span style={{ color: c.portalAdmin?.status === 'active' ? '#2e7d32' : '#e65100' }}>
                          {c.portalAdmin?.email || adminEmails[c.id]}
                          {' — '}
                          {c.portalAdmin?.status === 'active' ? 'Onboarded' : 'Invite sent'}
                        </span>
                        {c.portalAdmin?.lastLoginAt && (
                          <span style={{ color: '#888', marginLeft: '0.5rem' }}>
                            (last login: {fmtDate(c.portalAdmin.lastLoginAt)})
                          </span>
                        )}
                      </div>
                    )}
                    {!readOnly && (
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <input
                          style={{ ...s.input, flex: 1, padding: '0.4rem 0.6rem', fontSize: '0.82rem' }}
                          type="email"
                          placeholder="admin@company.com"
                          value={adminEmailInput[c.id] || ''}
                          onChange={e => setAdminEmailInput(prev => ({ ...prev, [c.id]: e.target.value }))}
                          onClick={e => e.stopPropagation()}
                        />
                        <button
                          style={{ ...s.btnSmall, background: '#A50B18', color: '#fff' }}
                          onClick={(e) => { e.stopPropagation(); setAdminEmail(c.id); }}
                          disabled={adminEmailStatus[c.id]?.loading}
                        >
                          {adminEmailStatus[c.id]?.loading ? 'Sending...' : 'Set Admin & Invite'}
                        </button>
                      </div>
                    )}
                    {adminEmailStatus[c.id]?.success && <div style={{ color: '#27ae60', fontSize: '0.78rem', marginTop: '0.4rem' }}>{adminEmailStatus[c.id].success}</div>}
                    {adminEmailStatus[c.id]?.error && <div style={{ color: '#e74c3c', fontSize: '0.78rem', marginTop: '0.4rem' }}>{adminEmailStatus[c.id].error}</div>}
                  </div>

                  {/* Employees added by this customer's admin */}
                  <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#fafafa', borderRadius: 8, border: '1px solid #e8e8e8' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#555', marginBottom: '0.5rem' }}>
                      Employees ({c.portalUserCount || 0})
                    </div>
                    {(!c.employees || c.employees.length === 0) ? (
                      <div style={{ fontSize: '0.82rem', color: '#888' }}>
                        {c.portalAdmin
                          ? 'No employees added yet — the admin hasn’t invited anyone.'
                          : 'Admin not set up — no employees can be added until the admin onboards.'}
                      </div>
                    ) : (
                      <table style={s.table}>
                        <thead>
                          <tr>
                            <th style={s.th}>Name</th>
                            <th style={s.th}>Email</th>
                            <th style={s.th}>Status</th>
                            <th style={s.th}>Can Buy Directly</th>
                            <th style={s.th}>Last Login</th>
                          </tr>
                        </thead>
                        <tbody>
                          {c.employees.map(e => (
                            <tr key={e.email}>
                              <td style={s.td}>{e.name || <span style={{ color: '#999' }}>—</span>}</td>
                              <td style={s.td}>{e.email}</td>
                              <td style={s.td}>
                                <span style={{
                                  ...s.badge,
                                  fontSize: '0.7rem',
                                  background: e.status === 'active' ? '#e8f5e9' : e.status === 'invited' ? '#fff3e0' : '#fafafa',
                                  color: e.status === 'active' ? '#2e7d32' : e.status === 'invited' ? '#e65100' : '#888',
                                }}>{e.status}</span>
                              </td>
                              <td style={s.td}>{e.canPurchaseDirectly ? 'Yes' : 'No'}</td>
                              <td style={s.td}>{e.lastLoginAt ? fmtDate(e.lastLoginAt) : <span style={{ color: '#999' }}>never</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {custOrders[c.id] && custOrders[c.id].length > 0 && (
                    <div style={{ marginTop: '0.75rem' }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#555', marginBottom: '0.5rem' }}>Recent Orders</div>
                      <table style={s.table}>
                        <thead><tr><th style={s.th}>Order #</th><th style={s.th}>Date</th><th style={s.thR}>Total</th></tr></thead>
                        <tbody>
                          {custOrders[c.id].map(o => (
                            <tr key={o.id}><td style={s.td}>{o.number}</td><td style={s.td}>{fmtDate(o.date)}</td><td style={s.tdR}>{fmt$(o.total)}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Detail views open in place. Notes are open to every role
                      (the one write techs/delivery have); the Order Guide
                      editor stays edit-role-only. The AI summary and account
                      health are on the sales site's Customer 360. */}
                  <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                    <button
                      style={{ ...s.btnOutline, fontSize: '0.85rem', padding: '0.6rem 1rem', color: '#A50B18', borderColor: '#A50B18', fontWeight: 600 }}
                      onClick={(e) => { e.stopPropagation(); setSynopsisCustomer(c); }}
                    >
                      Notes →
                    </button>
                    {!readOnly && (
                      <button
                        style={{ ...s.btnPrimary, fontSize: '0.85rem', padding: '0.6rem 1rem' }}
                        onClick={(e) => { e.stopPropagation(); setEditingCustomer(c); }}
                      >
                        Edit Order Guide →
                      </button>
                    )}
                    {canPlaceOrders && (
                      <button
                        style={{ ...s.btnPrimary, fontSize: '0.85rem', padding: '0.6rem 1rem', background: '#2e7d32' }}
                        onClick={(e) => { e.stopPropagation(); setOrderEntryCustomer(c); }}
                      >
                        Place Order →
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
          {filtered.length === 0 && <p style={s.sub}>No customers match</p>}
        </div>
      )}
    </div>
  );
}

// ── Order Guide Editor ──
// Standalone view for building/editing a customer's Order Guide. Rendered
// inside the Customers tab when an admin clicks "Edit Order Guide" on a row.
// Two-column: current guide on the left, full catalog browser on the right.
function OrderGuideEditor({ customer, onBack }) {
  const [items, setItems] = useState([]);
  const [hiddenItems, setHiddenItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogCategory, setCatalogCategory] = useState('All');

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      adminApi.getOrderGuide(customer.id),
      adminApi.getCatalog(),
    ]).then(([guide, cat]) => {
      if (cancelled) return;
      setItems(guide.items || []);
      setHiddenItems(guide.hiddenItems || []);
      setCatalog(cat.products || []);
    }).catch(e => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [customer.id]);

  // Warn on browser close if there are unsaved changes
  useEffect(() => {
    if (!dirty) return;
    const handler = e => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const inGuide = new Set(items.map(i => String(i.sosId)));
  const categories = [...new Set(catalog.map(p => p.category).filter(Boolean))].sort();

  const filteredCatalog = catalog.filter(p => {
    if (catalogCategory !== 'All' && p.category !== catalogCategory) return false;
    if (!catalogSearch) return true;
    const q = catalogSearch.toLowerCase();
    return (p.name || '').toLowerCase().includes(q) ||
           (p.sku || '').toLowerCase().includes(q) ||
           (p.brand || '').toLowerCase().includes(q) ||
           (p.description || '').toLowerCase().includes(q);
  });

  function addToGuide(p) {
    if (inGuide.has(String(p.id))) return;
    setHiddenItems(prev => prev.filter(h => String(h.sosId) !== String(p.id)));
    setItems(prev => [...prev, {
      sosId: String(p.id),
      sku: p.sku,
      name: p.name,
      category: p.category,
      parLevel: 0,
    }]);
    setDirty(true);
  }
  // Removed items are remembered rather than just dropped: an item the
  // customer has ordered before would otherwise come straight back from history.
  function removeFromGuide(it) {
    setItems(prev => prev.filter(i => String(i.sosId) !== String(it.sosId)));
    setHiddenItems(prev => [
      ...prev.filter(h => String(h.sosId) !== String(it.sosId)),
      { sosId: String(it.sosId), sku: it.sku, name: it.name, category: it.category, fromHistory: it.source === 'history' },
    ]);
    setDirty(true);
  }
  function restoreToGuide(h) {
    setHiddenItems(prev => prev.filter(x => String(x.sosId) !== String(h.sosId)));
    setItems(prev => [...prev, h.fromHistory
      ? { sosId: h.sosId, sku: h.sku, name: h.name, category: h.category, source: 'history' }
      : { sosId: h.sosId, sku: h.sku, name: h.name, category: h.category, parLevel: 0 }]);
    setDirty(true);
  }
  function updatePar(sosId, par) {
    const n = Math.max(0, parseInt(par) || 0);
    setItems(prev => prev.map(i => String(i.sosId) === String(sosId) ? { ...i, parLevel: n } : i));
    setDirty(true);
  }

  async function save() {
    setSaving(true);
    setError('');
    try {
      await adminApi.setOrderGuide(customer.id, items, hiddenItems);
      setSavedAt(new Date());
      setDirty(false);
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  function handleBack() {
    if (dirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    onBack();
  }

  const saveLabel = saving ? 'Saving...' : dirty ? 'Save Guide' : (savedAt ? 'Saved ✓' : 'Save Guide');

  return (
    <div>
      {/* Header */}
      <div style={s.ogeHeader}>
        <button style={s.ogeBack} onClick={handleBack}>← Back to customers</button>
        <div style={s.ogeTitleBlock}>
          <h2 style={s.ogeTitle}>{customer.name}</h2>
          <div style={s.ogeSubtitle}>
            Order Guide
            {!loading && <> · {items.length} item{items.length === 1 ? '' : 's'}</>}
            {dirty && <span style={s.ogeDirty}>• unsaved changes</span>}
          </div>
        </div>
        <button
          style={{
            ...s.btnPrimary,
            minWidth: 140,
            ...(dirty ? {} : { background: '#888', cursor: 'default' }),
          }}
          onClick={save}
          disabled={!dirty || saving}
        >
          {saveLabel}
        </button>
      </div>

      {error && <div style={s.loginError}>{error}</div>}

      {loading ? (
        <div style={s.sub}>Loading guide and catalog...</div>
      ) : (
        <div style={s.ogeGrid}>
          {/* Left: current guide */}
          <div style={s.ogePanel}>
            <div style={s.ogePanelHead}>
              <h3 style={s.ogePanelTitle}>Order Guide</h3>
              <span style={s.repsCount}>{items.length}</span>
            </div>
            {items.length === 0 ? (
              <div style={s.ogeEmpty}>
                No items yet. Add products from the catalog on the right to build this customer's starting order guide.
              </div>
            ) : (
              <div style={s.ogeItemsList}>
                {items.map(it => {
                  const isHistory = it.source === 'history';
                  return (
                    <div key={it.sosId} style={{ ...s.ogeItem, ...(isHistory ? s.ogeItemHistory : {}) }}>
                      <div style={s.ogeItemText}>
                        <div style={s.ogeItemName}>{it.name}</div>
                        <div style={s.ogeItemMeta}>
                          {it.sku && <span style={s.ogeSku}>{it.sku}</span>}
                          {it.category && <span style={s.ogeCat}>{it.category}</span>}
                          {isHistory && (
                            <span style={s.ogeHistoryTag}>
                              Previously ordered
                              {it.orderCount ? ` · ${it.orderCount}x` : ''}
                              {it.lastOrdered
                                ? ` · last ${new Date(it.lastOrdered).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                                : ''}
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={s.ogeItemControls}>
                        {!isHistory && (
                          <label style={s.ogeParLabel}>
                            PAR
                            <input
                              type="number"
                              min="0"
                              value={it.parLevel || 0}
                              onChange={e => updatePar(it.sosId, e.target.value)}
                              style={s.ogeParInput}
                            />
                          </label>
                        )}
                        <button
                          style={s.ogeRemoveBtn}
                          onClick={() => removeFromGuide(it)}
                          title="Remove from guide"
                          aria-label={`Remove ${it.name} from guide`}
                        >×</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {hiddenItems.length > 0 && (
              <div style={s.ogeRemovedBlock}>
                <div style={s.ogeRemovedHead}>
                  Removed from guide · {hiddenItems.length}
                </div>
                <div style={s.ogeRemovedNote}>
                  Hidden from this customer's guide even if they've ordered it before.
                </div>
                {hiddenItems.map(h => (
                  <div key={h.sosId} style={s.ogeRemovedItem}>
                    <div style={s.ogeItemText}>
                      <div style={s.ogeRemovedName}>{h.name || h.sku || h.sosId}</div>
                      <div style={s.ogeItemMeta}>
                        {h.sku && <span style={s.ogeSku}>{h.sku}</span>}
                        {h.category && <span style={s.ogeCat}>{h.category}</span>}
                      </div>
                    </div>
                    <button style={s.ogeAddBtn} onClick={() => restoreToGuide(h)}>Restore</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right: catalog browser */}
          <div style={s.ogePanel}>
            <div style={s.ogePanelHead}>
              <h3 style={s.ogePanelTitle}>Catalog</h3>
              <span style={s.repsCount}>{filteredCatalog.length}</span>
            </div>
            <input
              style={{ ...s.repsSearch, maxWidth: 'none', width: '100%', marginBottom: '0.5rem' }}
              placeholder="Search by name, SKU, brand, or product code..."
              value={catalogSearch}
              onChange={e => setCatalogSearch(e.target.value)}
            />
            <div style={{ ...s.catalogFilterRow, marginBottom: '0.75rem' }}>
              {['All', ...categories].map(cat => (
                <button
                  key={cat}
                  style={catalogCategory === cat ? { ...s.rangeBtn, ...s.rangeBtnActive } : s.rangeBtn}
                  onClick={() => setCatalogCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
            <div style={s.ogeCatalogList}>
              {filteredCatalog.length === 0 && (
                <div style={s.ogeEmpty}>No products match.</div>
              )}
              {filteredCatalog.map(p => {
                const added = inGuide.has(String(p.id));
                return (
                  <div key={p.id} style={s.ogeCatalogRow}>
                    {p.hasImage && p.sku ? (
                      <img
                        src={`/images/products/${p.sku}.jpg`}
                        alt=""
                        loading="lazy"
                        style={s.catalogThumb}
                        onError={e => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      <div style={s.catalogThumbEmpty}>—</div>
                    )}
                    <div style={s.ogeItemText}>
                      <div style={s.ogeItemName}>{p.name}</div>
                      <div style={s.ogeItemMeta}>
                        {p.sku && <span style={s.ogeSku}>{p.sku}</span>}
                        {p.category && <span style={s.ogeCat}>{p.category}</span>}
                        {p.price ? <span style={s.ogePrice}>${p.price.toFixed(2)}</span> : null}
                      </div>
                    </div>
                    {added ? (
                      <span style={s.ogeAddedBadge}>Added</span>
                    ) : (
                      <button style={s.ogeAddBtn} onClick={() => addToGuide(p)}>+ Add</button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Catalog Tab (visibility toggles) ──
// readOnly: sales reps can browse the full curated catalog (statuses,
// prices, overrides) but every mutating control is hidden — backend
// enforces the same via catalog:'read'.
function CatalogTab({ readOnly }) {
  const [products, setProducts] = useState([]);
  const [counts, setCounts] = useState({ total: 0, visible: 0, pending: 0, hidden: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  // 'all' | 'visible' | 'pending' | 'hidden'. Default 'pending' so the
  // first thing the admin sees is the items that need their attention.
  const [statusFilter, setStatusFilter] = useState('pending');
  const [saving, setSaving] = useState({}); // { [sku]: true } while toggle is in-flight
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null); // last sync summary
  // Category edit modal — set to a product object when open, null when closed.
  const [editingCategory, setEditingCategory] = useState(null);
  // Brand edit modal — same pattern.
  const [editingBrand, setEditingBrand] = useState(null);
  // Name/description edit modal — same pattern.
  const [editingText, setEditingText] = useState(null);
  const [editingPrice, setEditingPrice] = useState(null);
  // Image upload modal — same pattern.
  const [imageEditing, setImageEditing] = useState(null);

  async function reloadCatalog() {
    const d = await adminApi.getCatalog();
    setProducts(d.products || []);
    setCounts(d.counts || { total: (d.products || []).length, visible: 0, pending: 0, hidden: 0 });
  }

  useEffect(() => {
    adminApi.getCatalog()
      .then(d => {
        setProducts(d.products || []);
        setCounts(d.counts || { total: (d.products || []).length, visible: 0, pending: 0, hidden: 0 });
        // If there's nothing pending and nothing hidden, default to "all"
        // so the admin sees a non-empty list on first load.
        if ((d.counts?.pending || 0) === 0 && (d.counts?.hidden || 0) === 0) {
          setStatusFilter('all');
        }
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);


  async function handleInstantSync() {
    if (syncing) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      // Kick off the async sync. The POST returns immediately; actual work
      // runs in a self-invoked Lambda (takes ~60s because SOS throttles
      // bulk fetches). We poll the status endpoint until done/error.
      await adminApi.syncCatalog();
      const startedAt = Date.now();
      const POLL_MS = 2500;
      const TIMEOUT_MS = 4 * 60 * 1000; // 4 minutes upper bound
      while (true) {
        await new Promise(r => setTimeout(r, POLL_MS));
        let s;
        try {
          s = await adminApi.syncCatalogStatus();
        } catch (_) { continue; }
        if (s.status === 'done') {
          setSyncResult(s.result || {});
          await reloadCatalog();
          break;
        }
        if (s.status === 'error') {
          setSyncResult({ error: s.error || 'Sync failed' });
          break;
        }
        if (Date.now() - startedAt > TIMEOUT_MS) {
          setSyncResult({ error: 'Sync is taking longer than expected — check back in a minute.' });
          break;
        }
      }
    } catch (e) {
      setSyncResult({ error: e.message || 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  }

  const categories = useMemoCategories(products);
  const filtered = products.filter(p => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (categoryFilter !== 'All' && p.category !== categoryFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.name || '').toLowerCase().includes(q) ||
           (p.sku || '').toLowerCase().includes(q) ||
           (p.brand || '').toLowerCase().includes(q) ||
           (p.description || '').toLowerCase().includes(q);
  });

  // Persist a category/subcategory override edit from the modal. Empty
  // strings on either field clear the override (revert to SOS-parsed value).
  async function saveCategoryEdit({ sku, category, subCategory }) {
    setSaving(prev => ({ ...prev, [sku]: true }));
    const beforeRow = products.find(p => p.sku === sku);
    const apiCategory    = category    && category.trim()    ? category.trim()    : null;
    const apiSubCategory = subCategory && subCategory.trim() ? subCategory.trim() : null;
    // Optimistic update.
    setProducts(curr => curr.map(p => p.sku === sku ? {
      ...p,
      category: apiCategory ?? p.parsedCategory ?? p.category,
      subCategory: apiSubCategory ?? p.parsedSubCategory ?? null,
      categoryOverridden:    apiCategory    !== null,
      subCategoryOverridden: apiSubCategory !== null,
    } : p));
    try {
      await adminApi.setProductCategory(sku, { category: apiCategory, subCategory: apiSubCategory });
    } catch (e) {
      setProducts(curr => curr.map(p => p.sku === sku ? beforeRow : p));
      alert(`Failed to update category for ${sku}: ${e.message}`);
    } finally {
      setSaving(prev => { const n = { ...prev }; delete n[sku]; return n; });
    }
  }

  // Persist a brand override edit. Empty input clears the override and
  // falls back to the SOS Brand custom field (after alias normalization).
  async function saveBrandEdit({ sku, brand }) {
    setSaving(prev => ({ ...prev, [sku]: true }));
    const beforeRow = products.find(p => p.sku === sku);
    const apiBrand = brand && brand.trim() ? brand.trim() : null;
    // Optimistic update — falls back to parsedBrand if cleared.
    setProducts(curr => curr.map(p => p.sku === sku ? {
      ...p,
      brand: apiBrand ?? p.parsedBrand ?? null,
      brandOverridden: apiBrand !== null,
    } : p));
    try {
      await adminApi.setProductBrand(sku, apiBrand);
    } catch (e) {
      setProducts(curr => curr.map(p => p.sku === sku ? beforeRow : p));
      alert(`Failed to update brand for ${sku}: ${e.message}`);
    } finally {
      setSaving(prev => { const n = { ...prev }; delete n[sku]; return n; });
    }
  }

  // Persist a name/description override. A value matching SOS (or blank)
  // clears that override, so the item goes back to following SOS.
  async function saveTextEdit({ sku, name, description }) {
    setSaving(prev => ({ ...prev, [sku]: true }));
    const beforeRow = products.find(p => p.sku === sku);
    const asOverride = (value, parsed) => {
      const v = (value || '').trim();
      return v && v !== (parsed || '').trim() ? v : null;
    };
    const apiName = asOverride(name, beforeRow?.parsedName);
    const apiDescription = asOverride(description, beforeRow?.parsedDescription);
    setProducts(curr => curr.map(p => p.sku === sku ? {
      ...p,
      name: apiName ?? p.parsedName,
      nameOverridden: apiName !== null,
      description: apiDescription ?? p.parsedDescription ?? '',
      descriptionOverridden: apiDescription !== null,
    } : p));
    try {
      await adminApi.setProductText(sku, { name: apiName, description: apiDescription });
    } catch (e) {
      setProducts(curr => curr.map(p => p.sku === sku ? beforeRow : p));
      alert(`Failed to update name/description for ${sku}: ${e.message}`);
    } finally {
      setSaving(prev => { const n = { ...prev }; delete n[sku]; return n; });
    }
  }

  // Apply a status change with an optimistic update + rollback on failure.
  // nextStatus: 'visible' | 'hidden' | 'pending'. The Lambda accepts
  // visible: true | false | null with the same mapping.
  async function setStatus(sku, nextStatus) {
    setSaving(prev => ({ ...prev, [sku]: true }));
    const prev = products.find(p => p.sku === sku)?.status;
    setProducts(curr => curr.map(p => p.sku === sku ? { ...p, status: nextStatus, visible: nextStatus === 'visible' } : p));
    setCounts(c => ({
      ...c,
      [prev]: Math.max(0, (c[prev] || 0) - 1),
      [nextStatus]: (c[nextStatus] || 0) + 1,
    }));
    try {
      const apiVal = nextStatus === 'visible' ? true : nextStatus === 'hidden' ? false : null;
      await adminApi.setProductVisibility(sku, apiVal);
    } catch (e) {
      // Roll back on failure
      setProducts(curr => curr.map(p => p.sku === sku ? { ...p, status: prev, visible: prev === 'visible' } : p));
      setCounts(c => ({
        ...c,
        [nextStatus]: Math.max(0, (c[nextStatus] || 0) - 1),
        [prev]: (c[prev] || 0) + 1,
      }));
      alert(`Failed to update ${sku}: ${e.message}`);
    } finally {
      setSaving(s => { const n = { ...s }; delete n[sku]; return n; });
    }
  }

  return (
    <div>
      <div style={s.catalogHeader}>
        <div style={s.repsTitleRow}>
          <h2 style={s.repsTitle}>Catalog</h2>
          <span style={s.repsCount}>{products.length}</span>
        </div>
        <div style={s.catalogHeaderRight}>
          <span style={s.catalogHeaderNote}>
            <span style={{ color: '#2e7d32', fontWeight: 600 }}>{counts.visible}</span> visible
            {counts.pending > 0 && <> · <span style={{ color: '#b26500', fontWeight: 600 }}>{counts.pending}</span> pending</>}
            {counts.hidden > 0 && <> · <span style={{ color: 'rgba(0,0,0,0.45)', fontWeight: 600 }}>{counts.hidden}</span> hidden</>}
          </span>
          {!readOnly && (
            <button
              onClick={handleInstantSync}
              disabled={syncing}
              style={{ ...s.syncBtn, ...(syncing ? s.syncBtnBusy : {}) }}
              title="Pull the latest items, prices, and archives from SOS Inventory right now"
            >
              {syncing ? 'Syncing…' : 'Instant sync'}
            </button>
          )}
          <input
            style={s.repsSearch}
            placeholder="Search by name, SKU, brand, or product code..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {syncResult && (
        <div style={syncResult.error ? s.syncBannerErr : s.syncBannerOk}>
          {syncResult.error ? (
            <>❌ Sync failed: {syncResult.error}</>
          ) : (
            <>
              ✅ Synced in {(syncResult.durationMs / 1000).toFixed(1)}s —{' '}
              <b>{syncResult.total}</b> products live{' '}
              (<b>+{syncResult.added}</b> added,{' '}
              <b>−{syncResult.removedArchived}</b> archived,{' '}
              <b>−{syncResult.removedExcluded}</b> excluded
              {syncResult.removedEquipment ? <>, <b>−{syncResult.removedEquipment}</b> equipment</> : null}).
              {syncResult.sosChanged ? <SyncDriftList result={syncResult} /> : null}
            </>
          )}
        </div>
      )}

      {/* Status filter — Pending first because it's the action queue. */}
      <div style={s.catalogStatusRow}>
        {[
          { key: 'pending', label: `Pending review (${counts.pending})`, color: '#b26500' },
          { key: 'visible', label: `Visible (${counts.visible})`,        color: '#2e7d32' },
          { key: 'hidden',  label: `Hidden (${counts.hidden})`,          color: 'rgba(0,0,0,0.5)' },
          { key: 'all',     label: `All (${counts.total})`,              color: '#333' },
        ].map(opt => (
          <button
            key={opt.key}
            style={statusFilter === opt.key
              ? { ...s.statusBtn, ...s.statusBtnActive, border: `2px solid ${opt.color}`, color: opt.color }
              : s.statusBtn}
            onClick={() => setStatusFilter(opt.key)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div style={s.catalogFilterRow}>
        {['All', ...categories].map(cat => (
          <button
            key={cat}
            style={categoryFilter === cat ? { ...s.rangeBtn, ...s.rangeBtnActive } : s.rangeBtn}
            onClick={() => setCategoryFilter(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {loading && <div style={s.sub}>Loading catalog...</div>}
      {error && <div style={s.loginError}>{error}</div>}

      {!loading && !error && (
        <div style={s.repsTable}>
          <div style={s.repsTableHead}>
            <div style={{ flex: '0 0 72px' }}>Image</div>
            <div style={{ flex: '0 0 90px' }}>SKU</div>
            <div style={{ flex: 3 }}>Product</div>
            <div style={{ flex: '0 0 120px' }}>Brand</div>
            <div style={{ flex: '0 0 140px' }}>Category</div>
            <div style={{ flex: '0 0 100px', textAlign: 'right' }}>Price</div>
            <div style={{ flex: '0 0 220px', textAlign: 'center' }}>Status</div>
          </div>
          {filtered.length === 0 && (
            <div style={s.repsEmpty}>
              {statusFilter === 'pending' ? 'No items pending review.'
                : statusFilter === 'hidden' ? 'No items have been hidden.'
                : 'No products match your search.'}
            </div>
          )}
          {filtered.map(p => {
            const isSaving = !!saving[p.sku];
            const status = p.status || 'visible';
            const dim = status !== 'visible';
            // Cache-bust the thumbnail on the same render so a freshly-
            // uploaded replacement renders immediately instead of showing
            // the cached prior image.
            const thumbSrc = p.hasImage && p.sku
              ? `/images/products/${p.sku}.jpg${p._imgBust ? `?v=${p._imgBust}` : ''}`
              : null;
            return (
              <div key={p.sku || p.id} style={{ ...s.repsRow, opacity: dim ? 0.6 : 1 }}>
                <button
                  type="button"
                  style={{ ...s.catalogThumbBtn, flex: '0 0 72px', ...(readOnly ? { cursor: 'default' } : {}) }}
                  onClick={() => !readOnly && p.sku && setImageEditing(p)}
                  disabled={readOnly || !p.sku}
                  title={readOnly ? '' : thumbSrc ? 'Replace image' : 'Upload image'}
                >
                  {thumbSrc ? (
                    <img
                      src={thumbSrc}
                      alt=""
                      loading="lazy"
                      style={s.catalogThumb}
                      onError={e => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <div style={s.catalogThumbEmpty}>📷</div>
                  )}
                </button>
                <div style={{ ...s.repsCell, flex: '0 0 90px', fontFamily: 'monospace', fontSize: '0.82rem', color: 'rgba(0,0,0,0.55)' }}>
                  {p.sku || '—'}
                </div>
                <div style={{ ...s.repsCell, flex: 3, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontWeight: 500, color: '#333', ...(p.nameOverridden ? { fontStyle: 'italic', color: '#A50B18' } : null) }}>{p.name}</span>
                    {!readOnly && p.sku && (
                      <button
                        type="button"
                        style={s.catEditBtn}
                        title={p.nameOverridden || p.descriptionOverridden ? 'Edit name/description override' : 'Edit name/description'}
                        onClick={() => setEditingText(p)}
                      >
                        ✎
                      </button>
                    )}
                  </div>
                  {p.description && (
                    <div style={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.5)', marginTop: 2, ...(p.descriptionOverridden ? { fontStyle: 'italic', color: '#A50B18' } : null) }}>{p.description}</div>
                  )}
                </div>
                <div style={{ ...s.repsCell, flex: '0 0 120px', fontSize: '0.82rem', color: 'rgba(0,0,0,0.6)', position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={p.brandOverridden ? { fontStyle: 'italic', color: '#A50B18' } : null}>
                      {p.brand || '—'}
                    </span>
                    {!readOnly && (
                      <button
                        type="button"
                        style={s.catEditBtn}
                        title={p.brandOverridden ? 'Edit brand override' : 'Override brand'}
                        onClick={() => setEditingBrand(p)}
                      >
                        ✎
                      </button>
                    )}
                  </div>
                </div>
                <div style={{ ...s.repsCell, flex: '0 0 140px', fontSize: '0.82rem', color: 'rgba(0,0,0,0.6)', position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={p.categoryOverridden ? { fontStyle: 'italic', color: '#A50B18' } : null}>
                      {p.category || '—'}
                    </span>
                    {!readOnly && (
                      <button
                        type="button"
                        style={s.catEditBtn}
                        title={(p.categoryOverridden || p.subCategoryOverridden) ? 'Edit override' : 'Override category / subcategory'}
                        onClick={() => setEditingCategory(p)}
                      >
                        ✎
                      </button>
                    )}
                  </div>
                  {p.subCategory && (
                    <div style={{
                      color: p.subCategoryOverridden ? '#A50B18' : 'rgba(0,0,0,0.4)',
                      fontSize: '0.74rem',
                      fontStyle: p.subCategoryOverridden ? 'italic' : 'normal',
                    }}>
                      {p.subCategory}
                    </div>
                  )}
                </div>
                <div style={{ ...s.repsCell, flex: '0 0 100px', textAlign: 'right', fontFamily: 'monospace', color: '#A50B18' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                    <span>{p.price ? `$${p.price.toFixed(2)}` : '—'}</span>
                    {!readOnly && p.sosId && (
                      <button
                        type="button"
                        style={s.catEditBtn}
                        title="Edit list price / sales tax (saved to SOS)"
                        onClick={() => setEditingPrice(p)}
                      >
                        ✎
                      </button>
                    )}
                  </div>
                  {p.taxable === false && (
                    <div style={{ fontFamily: 'inherit', fontSize: '0.72rem', color: 'rgba(0,0,0,0.45)' }}>no sales tax</div>
                  )}
                </div>
                <div style={{ flex: '0 0 220px', textAlign: 'center', display: 'flex', gap: 4, justifyContent: 'center' }}>
                  {readOnly ? (
                    <span style={{
                      alignSelf: 'center', fontSize: '0.78rem', fontWeight: 600,
                      color: status === 'visible' ? '#2e7d32' : status === 'hidden' ? 'rgba(0,0,0,0.5)' : '#b26500',
                    }}>
                      {status === 'visible' ? '✓ Visible' : status === 'hidden' ? '✗ Hidden' : 'Pending review'}
                    </span>
                  ) : (
                    <StatusActions status={status} disabled={isSaving} onChange={next => setStatus(p.sku, next)} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editingCategory && (
        <CategoryEditModal
          product={editingCategory}
          onClose={() => setEditingCategory(null)}
          onSave={async vals => {
            await saveCategoryEdit({ sku: editingCategory.sku, ...vals });
            setEditingCategory(null);
          }}
        />
      )}

      {editingBrand && (
        <BrandEditModal
          product={editingBrand}
          onClose={() => setEditingBrand(null)}
          onSave={async vals => {
            await saveBrandEdit({ sku: editingBrand.sku, ...vals });
            setEditingBrand(null);
          }}
        />
      )}

      {editingPrice && (
        <ItemPriceEditModal
          product={editingPrice}
          onClose={() => setEditingPrice(null)}
          onSaved={({ price, taxable }) => {
            setProducts(curr => curr.map(p => p.sku === editingPrice.sku ? { ...p, price, taxable } : p));
            setEditingPrice(null);
          }}
        />
      )}

      {editingText && (
        <ProductTextEditModal
          product={editingText}
          onClose={() => setEditingText(null)}
          onSave={async vals => {
            await saveTextEdit({ sku: editingText.sku, ...vals });
            setEditingText(null);
          }}
        />
      )}

      {imageEditing && (
        <ProductImageUploadModal
          product={imageEditing}
          onClose={() => setImageEditing(null)}
          onSuccess={(result) => {
            // Optimistically update the row so the change shows
            // immediately. result.deleted=true means the admin removed
            // the image (hasImage:false); otherwise it's a new upload
            // and we use result.bust to break the browser's cache so
            // the new image renders instead of the old one.
            const wasDeleted = !!result?.deleted;
            setProducts(curr => curr.map(p =>
              p.sku === imageEditing.sku
                ? wasDeleted
                  ? { ...p, hasImage: false, _imgBust: undefined }
                  : { ...p, hasImage: true, _imgBust: result?.bust || Date.now() }
                : p
            ));
          }}
        />
      )}
    </div>
  );
}

const DRIFT_FIELD_LABELS = { price: 'price', description: 'description', taxable: 'taxable' };

function fmtDriftValue(field, v) {
  if (v == null) return '—';
  if (field === 'price') return fmt$(Number(v));
  if (field === 'taxable') return v ? 'yes' : 'no';
  return `“${v}”`;
}

// What the sync changed on items the catalog already had (SOS still wins for
// now — this is the report half of the item-master inversion).
function SyncDriftList({ result }) {
  const byField = Object.entries(result.sosChangedByField || {})
    .filter(([, n]) => n)
    .map(([f, n]) => `${n} ${DRIFT_FIELD_LABELS[f] || f}`)
    .join(', ');
  const sample = result.sosChangedSample || [];
  const totalChanges = Object.values(result.sosChangedByField || {}).reduce((a, n) => a + n, 0);
  return (
    <div style={{ marginTop: '0.4rem' }}>
      SOS changed <b>{result.sosChanged}</b> existing item{result.sosChanged === 1 ? '' : 's'} ({byField}):
      <ul style={s.syncDriftList}>
        {sample.map(c => (
          <li key={`${c.sku}-${c.field}`}>
            <b>{c.sku}</b> {c.name} — {DRIFT_FIELD_LABELS[c.field] || c.field}: {fmtDriftValue(c.field, c.from)} → {fmtDriftValue(c.field, c.to)}
          </li>
        ))}
      </ul>
      {totalChanges > sample.length && <div>…showing the first {sample.length} of {totalChanges} changes.</div>}
    </div>
  );
}

function CategoryEditModal({ product, onClose, onSave }) {
  // Pre-fill the inputs with whatever's effective right now (override or
  // SOS-parsed). Empty submission clears the override.
  const [category, setCategory] = useState(product.category || '');
  const [subCategory, setSubCategory] = useState(product.subCategory || '');
  const [busy, setBusy] = useState(false);

  function handleResetCategory() {
    setCategory(product.parsedCategory || '');
  }
  function handleResetSubCategory() {
    setSubCategory(product.parsedSubCategory || '');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await onSave({ category, subCategory });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={s.modalBackdrop} onClick={onClose}>
      <form style={s.catEditModal} onClick={e => e.stopPropagation()} onSubmit={handleSubmit}>
        <h3 style={{ margin: '0 0 0.25rem', fontSize: '1.05rem', color: '#333' }}>Edit category</h3>
        <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: 'rgba(0,0,0,0.55)' }}>
          {product.name}
          {product.sku && <span style={{ fontFamily: 'monospace', marginLeft: 6, color: 'rgba(0,0,0,0.4)' }}>{product.sku}</span>}
        </p>

        <label style={s.catEditField}>
          <span style={s.catEditLabel}>Category</span>
          <input
            style={s.catEditInput}
            value={category}
            onChange={e => setCategory(e.target.value)}
            placeholder={product.parsedCategory || 'Uncategorized'}
            autoFocus
          />
          <div style={s.catEditHint}>
            <span>SOS says: <code>{product.parsedCategory || '—'}</code></span>
            {category !== (product.parsedCategory || '') && (
              <button type="button" style={s.catEditResetLink} onClick={handleResetCategory}>
                Reset to SOS value
              </button>
            )}
          </div>
        </label>

        <label style={{ ...s.catEditField, marginTop: '0.85rem' }}>
          <span style={s.catEditLabel}>Subcategory <span style={{ color: 'rgba(0,0,0,0.35)', fontWeight: 400 }}>(optional)</span></span>
          <input
            style={s.catEditInput}
            value={subCategory}
            onChange={e => setSubCategory(e.target.value)}
            placeholder={product.parsedSubCategory || '(none)'}
          />
          <div style={s.catEditHint}>
            <span>SOS says: <code>{product.parsedSubCategory || '—'}</code></span>
            {subCategory !== (product.parsedSubCategory || '') && (
              <button type="button" style={s.catEditResetLink} onClick={handleResetSubCategory}>
                Reset to SOS value
              </button>
            )}
          </div>
        </label>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
          <button type="button" style={s.cancelBtn || { background: 'none', border: '1px solid #ddd', color: '#444', borderRadius: 6, padding: '0.55rem 1rem', fontSize: '0.85rem', cursor: 'pointer' }} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" style={s.saveBtn || { background: '#A50B18', color: '#fff', border: 'none', borderRadius: 6, padding: '0.55rem 1.2rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}

function BrandEditModal({ product, onClose, onSave }) {
  // Pre-fill with the effective brand (override or SOS-parsed). Empty input
  // on submit clears the override.
  const [brand, setBrand] = useState(product.brand || '');
  const [busy, setBusy] = useState(false);

  function handleResetBrand() {
    setBrand(product.parsedBrand || '');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await onSave({ brand });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={s.modalBackdrop} onClick={onClose}>
      <form style={s.catEditModal} onClick={e => e.stopPropagation()} onSubmit={handleSubmit}>
        <h3 style={{ margin: '0 0 0.25rem', fontSize: '1.05rem', color: '#333' }}>Edit brand</h3>
        <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: 'rgba(0,0,0,0.55)' }}>
          {product.name}
          {product.sku && <span style={{ fontFamily: 'monospace', marginLeft: 6, color: 'rgba(0,0,0,0.4)' }}>{product.sku}</span>}
        </p>

        <label style={s.catEditField}>
          <span style={s.catEditLabel}>Brand</span>
          <input
            style={s.catEditInput}
            value={brand}
            onChange={e => setBrand(e.target.value)}
            placeholder={product.parsedBrand || '(none)'}
            autoFocus
          />
          <div style={s.catEditHint}>
            <span>SOS says: <code>{product.parsedBrand || '—'}</code></span>
            {brand !== (product.parsedBrand || '') && (
              <button type="button" style={s.catEditResetLink} onClick={handleResetBrand}>
                Reset to SOS value
              </button>
            )}
          </div>
          <div style={{ ...s.catEditHint, marginTop: 4, color: 'rgba(0,0,0,0.45)' }}>
            Leave blank and save to clear the override entirely.
          </div>
        </label>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
          <button type="button" style={{ background: 'none', border: '1px solid #ddd', color: '#444', borderRadius: 6, padding: '0.55rem 1rem', fontSize: '0.85rem', cursor: 'pointer' }} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" style={{ background: '#A50B18', color: '#fff', border: 'none', borderRadius: 6, padding: '0.55rem 1.2rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}

function ProductTextEditModal({ product, onClose, onSave }) {
  // Pre-fill with what customers see now (override or SOS). Saving a value
  // that matches SOS, or a blank one, clears that override.
  const [name, setName] = useState(product.name || '');
  const [description, setDescription] = useState(product.description || '');
  const [busy, setBusy] = useState(false);
  const parsedName = product.parsedName || '';
  const parsedDescription = product.parsedDescription || '';

  async function handleSubmit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await onSave({ name, description });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={s.modalBackdrop} onClick={onClose}>
      <form style={s.catEditModal} onClick={e => e.stopPropagation()} onSubmit={handleSubmit}>
        <h3 style={{ margin: '0 0 0.25rem', fontSize: '1.05rem', color: '#333' }}>Edit name &amp; description</h3>
        <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: 'rgba(0,0,0,0.55)' }}>
          {product.sku && <span style={{ fontFamily: 'monospace', color: 'rgba(0,0,0,0.4)' }}>{product.sku}</span>}
          {' '}— what customers see in the catalog. The SOS sync no longer overwrites what you set here.
        </p>

        <label style={s.catEditField}>
          <span style={s.catEditLabel}>Name</span>
          <input
            style={s.catEditInput}
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={parsedName}
            maxLength={200}
            autoFocus
          />
          <div style={s.catEditHint}>
            <span>SOS says: <code>{parsedName || '—'}</code></span>
            {name !== parsedName && (
              <button type="button" style={s.catEditResetLink} onClick={() => setName(parsedName)}>
                Reset to SOS value
              </button>
            )}
          </div>
        </label>

        <label style={{ ...s.catEditField, marginTop: '0.9rem' }}>
          <span style={s.catEditLabel}>Description</span>
          <textarea
            style={{ ...s.catEditInput, minHeight: 80, resize: 'vertical', fontFamily: 'inherit' }}
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder={parsedDescription}
            maxLength={2000}
          />
          <div style={s.catEditHint}>
            <span>SOS says: <code>{parsedDescription || '—'}</code></span>
            {description !== parsedDescription && (
              <button type="button" style={s.catEditResetLink} onClick={() => setDescription(parsedDescription)}>
                Reset to SOS value
              </button>
            )}
          </div>
          <div style={{ ...s.catEditHint, marginTop: 4, color: 'rgba(0,0,0,0.45)' }}>
            This text also goes onto new SOS order lines for this item. Leave blank to follow SOS again.
          </div>
        </label>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
          <button type="button" style={{ background: 'none', border: '1px solid #ddd', color: '#444', borderRadius: 6, padding: '0.55rem 1rem', fontSize: '0.85rem', cursor: 'pointer' }} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" style={{ background: '#A50B18', color: '#fff', border: 'none', borderRadius: 6, padding: '0.55rem 1.2rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}

function ItemPriceEditModal({ product, onClose, onSaved }) {
  const initialPriceText = product.price ? product.price.toFixed(2) : '';
  const initialTaxable = product.taxable !== false;
  const [priceText, setPriceText] = useState(initialPriceText);
  const [taxable, setTaxable] = useState(initialTaxable);
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const price = Number(priceText);
  const priceValid = /^\d+(\.\d{1,2})?$/.test(priceText.trim()) && price > 0;
  const money = n => `$${Number(n).toFixed(2)}`;
  // Only what the admin changed goes to the server. The portal's copy can be
  // behind SOS, so sending an untouched field would write that old value back.
  const priceTouched = priceText.trim() !== initialPriceText;
  const taxTouched = taxable !== initialTaxable;
  const changes = { ...(priceTouched ? { price } : {}), ...(taxTouched ? { taxable } : {}) };
  const canPreview = !busy && (!priceTouched || priceValid);

  function edited(fn) {
    return e => { fn(e); setPreview(null); setError(''); };
  }

  async function runPreview() {
    if (!canPreview) return;
    setBusy(true);
    setError('');
    try {
      const r = await adminApi.setItemPrice(product.sku, { ...changes, dryRun: true });
      setPreview(r.preview);
      setHistory(r.history || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (!preview || busy) return;
    setBusy(true);
    setError('');
    try {
      const r = await adminApi.setItemPrice(product.sku, { ...changes, reason });
      if (r.catalogPatched === false) {
        alert(r.saved ? 'Saved in SOS. The portal will show it after the next Instant sync.' : "The portal's copy couldn't be updated. Run Instant sync to bring it in line with SOS.");
      }
      onSaved(r.portal || { price, taxable });
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  }

  const blocked = preview && preview.blockers.length > 0;
  const portalOnly = preview && preview.noChange && !!preview.portalOutOfStep;
  const canSave = preview && !blocked && (!preview.noChange || portalOnly) && !busy;

  return (
    <div style={s.modalBackdrop} onClick={onClose}>
      <div style={{ ...s.catEditModal, maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 0.25rem', fontSize: '1.05rem', color: '#333' }}>Edit price &amp; sales tax</h3>
        <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: 'rgba(0,0,0,0.55)' }}>
          <span style={{ fontFamily: 'monospace', color: 'rgba(0,0,0,0.4)' }}>{product.sku}</span> {product.name}
          <br />Saved straight into SOS, which passes it on to QuickBooks. Tier prices are not changed here.
        </p>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ ...s.catEditField, flex: '0 0 160px' }}>
            <span style={s.catEditLabel}>List price</span>
            <input
              style={s.catEditInput}
              inputMode="decimal"
              value={priceText}
              onChange={edited(e => setPriceText(e.target.value.replace(/[^\d.]/g, '')))}
              autoFocus
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.88rem', paddingBottom: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={taxable} onChange={edited(e => setTaxable(e.target.checked))} />
            Charge sales tax
          </label>
        </div>
        {priceText && !priceValid && (
          <div style={{ ...s.catEditHint, color: '#A50B18' }}>Enter a price above zero, with up to 2 decimals.</div>
        )}

        <label style={{ ...s.catEditField, marginTop: '0.9rem' }}>
          <span style={s.catEditLabel}>Reason (optional)</span>
          <input style={s.catEditInput} value={reason} onChange={e => setReason(e.target.value)} maxLength={300} placeholder="e.g. January price list" />
        </label>

        {preview && (
          <div style={{ marginTop: '1rem', padding: '0.75rem 0.9rem', background: '#faf8f6', border: '1px solid #eee', borderRadius: 6, fontSize: '0.86rem', lineHeight: 1.5 }}>
            {preview.noChange && !portalOnly && <div>Nothing to change — SOS and the portal already have these values.</div>}
            {portalOnly && <div>SOS already has these values. Saving updates the portal's copy to match SOS.</div>}
            {preview.price && (
              <div>
                <strong>List price {money(preview.price.from)} → {money(preview.price.to)}</strong>
                {preview.price.changePct != null && ` (${preview.price.changePct > 0 ? '+' : ''}${preview.price.changePct}%)`}
                <div style={{ color: 'rgba(0,0,0,0.6)' }}>Paid by {preview.listCustomers} customer{preview.listCustomers === 1 ? '' : 's'} on list price.</div>
              </div>
            )}
            {preview.price && preview.tiers.map(t => (
              <div key={t.tierId} style={{ color: 'rgba(0,0,0,0.6)' }}>
                {t.name} ({t.customers} customer{t.customers === 1 ? '' : 's'}):{' '}
                {t.from === t.to ? `stays ${money(t.to)}` : `${money(t.from)} → ${money(t.to)}`}
                {t.whenPortalPricingOn != null && ` — would be ${money(t.whenPortalPricingOn)} once portal pricing is turned on`}
              </div>
            ))}
            {preview.special && (
              <div style={{ color: 'rgba(0,0,0,0.6)' }}>
                {preview.special.label} ({preview.special.percent}% off): sale price {money(preview.special.from)} → {money(preview.special.to)}
              </div>
            )}
            {preview.taxable && (
              <div style={{ marginTop: 4 }}>
                <strong>{preview.taxable.to ? 'Sales tax will be charged' : 'Sales tax will no longer be charged'}</strong> on this item on new orders. Past orders don't change.
              </div>
            )}
            {preview.warnings.map(w => <div key={w} style={{ color: '#b26500', marginTop: 4 }}>⚠ {w}</div>)}
            {preview.blockers.map(b => <div key={b} style={{ color: '#A50B18', marginTop: 4, fontWeight: 600 }}>✕ {b}</div>)}
            {history.length > 0 && (
              <div style={{ marginTop: 8, fontSize: '0.78rem', color: 'rgba(0,0,0,0.5)' }}>
                Last changes: {history.map(h => `${String(h.at).slice(0, 10)} ${h.by}${h.action === 'portal-sync' ? ' portal caught up with SOS' : ''}${h.newPrice != null ? ` ${money(h.oldPrice)}→${money(h.newPrice)}` : ''}${h.newTaxable != null ? ` tax ${h.newTaxable ? 'on' : 'off'}` : ''}`).join(' · ')}
              </div>
            )}
          </div>
        )}

        {error && <div style={{ marginTop: '0.75rem', color: '#A50B18', fontSize: '0.85rem' }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
          <button type="button" style={{ background: 'none', border: '1px solid #ddd', color: '#444', borderRadius: 6, padding: '0.55rem 1rem', fontSize: '0.85rem', cursor: 'pointer' }} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          {!preview ? (
            <button type="button" style={{ background: '#333', color: '#fff', border: 'none', borderRadius: 6, padding: '0.55rem 1.2rem', fontSize: '0.85rem', fontWeight: 600, cursor: canPreview ? 'pointer' : 'not-allowed', opacity: canPreview ? 1 : 0.5 }} onClick={runPreview} disabled={!canPreview}>
              {busy ? 'Checking…' : 'Preview'}
            </button>
          ) : (
            <button type="button" style={{ background: '#A50B18', color: '#fff', border: 'none', borderRadius: 6, padding: '0.55rem 1.2rem', fontSize: '0.85rem', fontWeight: 600, cursor: canSave ? 'pointer' : 'not-allowed', opacity: canSave ? 1 : 0.5 }} onClick={save} disabled={!canSave}>
              {portalOnly ? (busy ? 'Updating…' : 'Update the portal') : (busy ? 'Saving to SOS…' : 'Save to SOS')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Toggle({ checked, disabled, onChange }) {
  const bg = checked ? '#2e7d32' : '#c7c4c0';
  return (
    <button
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      aria-pressed={checked}
      style={{
        width: 44, height: 24, borderRadius: 12,
        background: bg, border: 'none', padding: 0,
        cursor: disabled ? 'wait' : 'pointer',
        position: 'relative', transition: 'background 0.15s',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <span style={{
        position: 'absolute',
        top: 3, left: checked ? 23 : 3,
        width: 18, height: 18, borderRadius: '50%',
        background: '#fff', transition: 'left 0.15s',
        boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
      }} />
    </button>
  );
}

function useMemoCategories(products) {
  // Keep this trivial — products list changes rarely; cheap to recompute.
  const set = new Set();
  products.forEach(p => { if (p.category) set.add(p.category); });
  return [...set].sort();
}

// Three-state action group for catalog curation.
//   pending → [Approve] [Hide]
//   visible → [✓ Visible] [Hide]
//   hidden  → [Approve] [✗ Hidden]
// The non-current actions are clickable; the current one is shown disabled
// as a label. Reset-to-pending is intentionally not exposed here — admins
// set hidden if they want it off, approved if they want it on.
function StatusActions({ status, disabled, onChange }) {
  const isVisible = status === 'visible';
  const isHidden  = status === 'hidden';
  return (
    <>
      <button
        onClick={() => !disabled && !isVisible && onChange('visible')}
        disabled={disabled || isVisible}
        title={isVisible ? 'Currently visible to customers' : 'Approve — make visible to customers'}
        style={{
          padding: '0.35rem 0.7rem', borderRadius: 5, border: '1px solid', cursor: disabled || isVisible ? 'default' : 'pointer',
          background: isVisible ? '#2e7d32' : '#fff', borderColor: isVisible ? '#2e7d32' : '#cfe6d3',
          color: isVisible ? '#fff' : '#2e7d32', fontSize: '0.78rem', fontWeight: 600, fontFamily: 'inherit',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {isVisible ? '✓ Visible' : 'Approve'}
      </button>
      <button
        onClick={() => !disabled && !isHidden && onChange('hidden')}
        disabled={disabled || isHidden}
        title={isHidden ? 'Currently hidden from customers' : 'Hide — keep out of customer catalog'}
        style={{
          padding: '0.35rem 0.7rem', borderRadius: 5, border: '1px solid', cursor: disabled || isHidden ? 'default' : 'pointer',
          background: isHidden ? '#c7c4c0' : '#fff', borderColor: isHidden ? '#c7c4c0' : '#e2ddd7',
          color: isHidden ? '#fff' : 'rgba(0,0,0,0.55)', fontSize: '0.78rem', fontWeight: 600, fontFamily: 'inherit',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {isHidden ? '✗ Hidden' : 'Hide'}
      </button>
    </>
  );
}

// ── Representatives Tab ──
// canManage (owners only): shows a role picker per person plus Remove/Restore;
// owners can't change or remove their own account. Receipt Capturer is
// assignable so field-staff accounts convert to portal personas (and back)
// right here. Removing someone who left disables the account rather than
// deleting it — their name stays on past receipts, notes and orders.
const ASSIGNABLE_ROLES = ['owner', 'silver_service_admin', 'sales_rep', 'technician', 'delivery', 'receipt_capturer'];
function RepresentativesTab({ canManage, myEmail }) {
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [savingRole, setSavingRole] = useState(''); // email while a change is in-flight
  const [savingStatus, setSavingStatus] = useState('');
  const [showRemoved, setShowRemoved] = useState(false);
  const [sendingInvite, setSendingInvite] = useState('');
  const [inviteLink, setInviteLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newPerson, setNewPerson] = useState({ name: '', email: '', role: '' });
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState('');

  useEffect(() => {
    adminApi.listRepresentatives()
      .then(d => setReps(d.representatives || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  async function changeRole(rep, role) {
    if (role === rep.role) return;
    const label = ROLE_LABELS[role] || role;
    if (!window.confirm(`Change ${rep.name || rep.email} to ${label}? Their portal access updates immediately.`)) return;
    setSavingRole(rep.email);
    try {
      const d = await adminApi.setRepresentativeRole(rep.email, role);
      const updated = d.representative;
      setReps(curr => curr.map(r => r.email === rep.email ? { ...r, ...updated } : r));
    } catch (e) {
      alert(`Failed to change role: ${e.message}`);
    } finally {
      setSavingRole('');
    }
  }

  async function changeStatus(rep, status) {
    const who = rep.name || rep.email;
    const prompt = status === 'disabled'
      ? `Remove ${who}? They lose access to the admin portal, the receipt capture app and the sales tool right away. Their name stays on past receipts and notes, and you can restore them here later.`
      : `Restore access for ${who}? They'll be able to log in again as ${ROLE_LABELS[rep.role] || rep.role}.`;
    if (!window.confirm(prompt)) return;
    setSavingStatus(rep.email);
    try {
      const d = await adminApi.setRepresentativeStatus(rep.email, status);
      const updated = d.representative;
      setReps(curr => curr.map(r => r.email === rep.email ? { ...r, ...updated } : r));
    } catch (e) {
      alert(`Failed to update ${who}: ${e.message}`);
    } finally {
      setSavingStatus('');
    }
  }

  // Staff accounts are created by hand, so the only way to get someone in is a
  // setup link. It doubles as a password reset for people who are already
  // active — hence the wording shift.
  async function resendInvite(rep) {
    const who = rep.name || rep.email;
    const what = rep.status === 'active'
      ? 'set a new password'
      : 'finish setting up their account';
    if (!window.confirm(`Create a setup link for ${who}? Anyone holding the link can ${what}, so send it straight to them. It stops working after 14 days.`)) return;
    setSendingInvite(rep.email);
    try {
      const d = await adminApi.resendRepresentativeInvite(rep.email);
      setInviteLink(d);
      setCopied(false);
    } catch (e) {
      alert(`Could not create a setup link for ${who}: ${e.message}`);
    } finally {
      setSendingInvite('');
    }
  }

  // Adding someone creates their staff record and hands back the same setup
  // link the row button makes, so it opens in the same box.
  async function addPerson(e) {
    e.preventDefault();
    setAddBusy(true); setAddError('');
    try {
      const d = await adminApi.addRepresentative(newPerson);
      setReps(curr => [d.representative, ...curr]);
      setInviteLink({ ...d, added: true });
      setCopied(false);
      setNewPerson({ name: '', email: '', role: '' });
      setAdding(false);
    } catch (err) {
      setAddError(err.message);
    } finally {
      setAddBusy(false);
    }
  }

  async function copyInviteLink() {
    try {
      await navigator.clipboard.writeText(inviteLink.setupUrl);
      setCopied(true);
    } catch {
      // Clipboard access can be refused; the URL is selectable in the box.
      setCopied(false);
      alert('Could not copy automatically — select the link and copy it.');
    }
  }

  const removedCount = reps.filter(r => r.status === 'disabled').length;
  const activeCount = reps.length - removedCount;
  const filtered = reps.filter(r => {
    if (r.status === 'disabled' && !showRemoved) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.name || '').toLowerCase().includes(q) ||
           (r.email || '').toLowerCase().includes(q);
  });

  function initialsOf(name, email) {
    return (name || email || '?')
      .split(/[\s@.]+/).filter(Boolean).slice(0, 2).map(p => p[0]?.toUpperCase()).join('');
  }

  return (
    <div>
      <div style={s.repsHeader}>
        <div style={s.repsTitleRow}>
          <h2 style={s.repsTitle}>Representatives</h2>
          <span style={s.repsCount}>{activeCount}</span>
          {removedCount > 0 && (
            <button style={s.repsRemovedToggle} onClick={() => setShowRemoved(v => !v)}>
              {showRemoved ? 'Hide' : 'Show'} removed ({removedCount})
            </button>
          )}
        </div>
        <input
          style={s.repsSearch}
          placeholder="Search by name or email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {canManage && !adding && (
          <button style={s.repsAddBtn} onClick={() => { setAdding(true); setAddError(''); }}>+ Add a person</button>
        )}
      </div>

      {canManage && adding && (
        <form style={s.repsAddPanel} onSubmit={addPerson}>
          <div style={s.repsAddTitle}>Add someone to the company</div>
          <div style={s.repsAddFields}>
            <label style={s.repsAddField}>
              <span style={s.repsAddLabel}>Name</span>
              <input style={s.repsAddInput} value={newPerson.name} autoFocus required
                onChange={e => setNewPerson(p => ({ ...p, name: e.target.value }))} placeholder="Jacob Fenton" />
            </label>
            <label style={s.repsAddField}>
              <span style={s.repsAddLabel}>Work email</span>
              <input style={s.repsAddInput} type="email" value={newPerson.email} required
                onChange={e => setNewPerson(p => ({ ...p, email: e.target.value }))} placeholder="name@silverservice.com" />
            </label>
            <label style={s.repsAddField}>
              <span style={s.repsAddLabel}>Role</span>
              <select style={s.repsAddInput} value={newPerson.role} required
                onChange={e => setNewPerson(p => ({ ...p, role: e.target.value }))}>
                <option value="" disabled>Choose a role…</option>
                {ASSIGNABLE_ROLES.map(role => <option key={role} value={role}>{ROLE_LABELS[role] || role}</option>)}
              </select>
            </label>
          </div>
          {newPerson.role && <div style={s.repsAddHint}>{ROLE_OPENS[newPerson.role]}</div>}
          {addError && <div style={s.loginError}>{addError}</div>}
          <div style={s.repsAddActions}>
            <button type="button" style={s.repsAddCancel} onClick={() => { setAdding(false); setAddError(''); }}>Cancel</button>
            <button type="submit" style={s.repsAddSubmit} disabled={addBusy}>{addBusy ? 'Adding…' : 'Add and make setup link'}</button>
          </div>
        </form>
      )}

      {loading && <div style={s.sub}>Loading...</div>}
      {error && <div style={s.loginError}>{error}</div>}

      {!loading && !error && (
        <div style={s.repsTable}>
          <div style={s.repsTableHead}>
            <div style={{ flex: 2 }}>Name</div>
            <div style={{ flex: 2 }}>Contact Email</div>
            <div style={{ flex: 1 }}>Phone</div>
            <div style={{ flex: 1 }}>Status</div>
            <div style={{ flex: 1 }}>Permission</div>
            {canManage && <div style={{ flex: 1 }}>Access</div>}
          </div>
          {filtered.length === 0 && (
            <div style={s.repsEmpty}>No representatives found.</div>
          )}
          {filtered.map(r => {
            const removed = r.status === 'disabled';
            // Owners (the hardcoded business-owner emails resolve to this role)
            // can't be removed here — demote first if that's really the intent.
            const canRemove = canManage && r.email !== myEmail && r.role !== 'owner';
            return (
            <div key={r.email} style={removed ? { ...s.repsRow, ...s.repsRowRemoved } : s.repsRow}>
              <div style={{ ...s.repsCell, flex: 2, display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <div style={removed ? { ...s.repsAvatar, background: '#b3aea8' } : s.repsAvatar}>{initialsOf(r.name, r.email)}</div>
                <div>{r.name || '—'}</div>
              </div>
              <div style={{ ...s.repsCell, flex: 2, fontFamily: 'monospace', fontSize: '0.85rem' }}>{r.email}</div>
              <div style={{ ...s.repsCell, flex: 1 }}>{r.phone || '—'}</div>
              <div style={{ ...s.repsCell, flex: 1 }}>
                <span style={removed ? s.statusRemoved : r.status === 'active' ? s.statusActive : s.statusInvited}>
                  {removed ? 'Removed' : r.status === 'active' ? 'Active' : r.status === 'invited' ? 'Invited' : r.status}
                </span>
              </div>
              <div style={{ ...s.repsCell, flex: 1 }}>
                {canManage && r.email !== myEmail && !removed ? (
                  <select
                    value={r.role}
                    disabled={savingRole === r.email}
                    onChange={e => changeRole(r, e.target.value)}
                    style={{ padding: '0.3rem 0.4rem', borderRadius: 6, border: '1px solid #ddd', fontSize: '0.82rem', background: '#fff' }}
                  >
                    {ASSIGNABLE_ROLES.map(role => (
                      <option key={role} value={role}>{ROLE_LABELS[role] || role}</option>
                    ))}
                  </select>
                ) : (
                  r.permission || ROLE_LABELS[r.role] || 'Admin'
                )}
              </div>
              {canManage && (
                <div style={{ ...s.repsCell, flex: 1, display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  {!removed && (
                    <button
                      style={s.repsInviteBtn}
                      disabled={sendingInvite === r.email}
                      onClick={() => resendInvite(r)}
                      title={r.status === 'active'
                        ? 'Create a link that lets them set a new password'
                        : 'Create a link that lets them finish setting up'}
                    >
                      {sendingInvite === r.email ? 'Working…' : 'Setup link'}
                    </button>
                  )}
                  {canRemove ? (
                    <button
                      style={removed ? s.repsRestoreBtn : s.repsRemoveBtn}
                      disabled={savingStatus === r.email}
                      onClick={() => changeStatus(r, removed ? 'active' : 'disabled')}
                    >
                      {savingStatus === r.email ? 'Saving…' : removed ? 'Restore' : 'Remove'}
                    </button>
                  ) : (
                    removed && <span style={s.repsNoAction}>—</span>
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {inviteLink && (
        <div style={s.modalBackdrop} onClick={() => setInviteLink(null)}>
          <div style={s.inviteModal} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.25rem', fontSize: '1.05rem', color: '#333' }}>
              {inviteLink.added ? `${inviteLink.name || inviteLink.email} is added. Send them this setup link` : `Setup link for ${inviteLink.name || inviteLink.email}`}
            </h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: 'rgba(0,0,0,0.55)' }}>
              Email or text this to them. It expires {new Date(inviteLink.expiresAt).toLocaleDateString()},
              and it's the only thing they need — they'll pick their own password and land straight in the
              right app.
            </p>
            <textarea style={s.inviteUrlBox} readOnly value={inviteLink.setupUrl} onFocus={e => e.target.select()} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <button
                style={{ background: 'none', border: '1px solid #ddd', color: '#444', borderRadius: 6, padding: '0.55rem 1rem', fontSize: '0.85rem', cursor: 'pointer' }}
                onClick={() => setInviteLink(null)}
              >
                Done
              </button>
              <button
                style={{ background: '#A50B18', color: '#fff', border: 'none', borderRadius: 6, padding: '0.55rem 1.2rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
                onClick={copyInviteLink}
              >
                {copied ? '✓ Copied' : 'Copy link'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Activity Tab ──
// Order audit log — who placed/edited which orders, and what changed.
// Reads from the OrderAudit DynamoDB table via /admin/order-audit.
function ActivityTab() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    adminApi.getOrderAudit({ limit: 200 })
      .then(res => setRecords(res.records || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={s.section}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ ...s.sectionTitle, margin: 0 }}>Order Activity Log</h3>
        <button style={s.btnOutline} onClick={load} disabled={loading}>{loading ? 'Loading...' : 'Refresh'}</button>
      </div>
      {error && <p style={s.errorText}>{error}</p>}
      {!error && !loading && records.length === 0 && <p style={s.sub}>No order activity recorded yet.</p>}
      {records.length > 0 && (
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>When</th>
              <th style={s.th}>Action</th>
              <th style={s.th}>Order</th>
              <th style={s.th}>By</th>
              <th style={s.th}>Changes</th>
            </tr>
          </thead>
          <tbody>
            {records.map((r, i) => (
              <tr key={`${r.orderId}-${r.timestamp}-${i}`}>
                <td style={s.td}>{new Date(r.timestamp).toLocaleString('en-US', { timeZone: 'America/Denver' })}</td>
                <td style={s.td}>{r.action === 'place' ? 'Placed' : r.action === 'edit' ? 'Edited' : r.action}</td>
                <td style={s.td}>{r.orderNumber || r.orderId}</td>
                <td style={s.td}>{r.customerName || r.userName || r.userEmail || '—'}</td>
                <td style={s.td}>{r.changeSummary || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Customer Crosswalk Tab ──
// Curates the unified customer master for the sales BI tool: review the
// matcher's near-miss suggestions and confirm or reject each. Owner-only.
const SYSTEM_LABEL = { sos: 'SOS', qbo: 'QuickBooks', hubspot: 'HubSpot', sage: 'Sage 50' };
const pairKey = (a, b) => `${a.system}:${a.id}|${b.system}:${b.id}`;

// Manually link a SOS customer to a HubSpot company when their names differ too
// much for auto-matching (e.g. "Art Hotel - DNS" vs "The ART Hotel Denver Curio
// Collection by Hilton"). Reuses the confirm endpoint with an explicit pair.
function ManualLink({ sosOnly, hubspotOnly, onLinked }) {
  const [sosQ, setSosQ] = useState('');
  const [hsQ, setHsQ] = useState('');
  const [sosPick, setSosPick] = useState(null);
  const [hsPick, setHsPick] = useState(null);
  const [linking, setLinking] = useState(false);
  const [err, setErr] = useState('');
  const [okMsg, setOkMsg] = useState('');

  const sosMatches = sosQ.trim() ? sosOnly.filter(c => c.name.toLowerCase().includes(sosQ.toLowerCase().trim())).slice(0, 8) : [];
  const hsMatches = hsQ.trim() ? hubspotOnly.filter(c => c.name.toLowerCase().includes(hsQ.toLowerCase().trim())).slice(0, 8) : [];

  async function link() {
    if (!sosPick || !hsPick) return;
    setLinking(true); setErr(''); setOkMsg('');
    try {
      const res = await adminApi.confirmCrosswalkMatch({
        a: { system: 'sos', id: sosPick.id, name: sosPick.name },
        b: { system: 'hubspot', id: hsPick.id, name: hsPick.name },
      });
      setOkMsg(`Linked “${sosPick.name}” ↔ “${hsPick.name}”. It now counts as one account.`);
      setSosPick(null); setHsPick(null); setSosQ(''); setHsQ('');
      onLinked(res);
    } catch (e) { setErr(e.message); } finally { setLinking(false); }
  }

  const picker = (label, placeholder, query, setQuery, matches, pick, setPick, count) => (
    <div style={{ flex: 1, minWidth: 260 }}>
      <div style={s.fieldLabel}>{label} <span style={s.sub}>({count} unmatched)</span></div>
      {pick ? (
        <div style={s.linkChip}>{pick.name}<button style={s.linkChipX} onClick={() => setPick(null)}>×</button></div>
      ) : (
        <>
          <input style={s.smallInput} placeholder={placeholder} value={query} onChange={e => setQuery(e.target.value)} />
          {matches.map(c => (
            <div key={c.id} style={s.linkRow} onClick={() => { setPick(c); setQuery(''); }}>{c.name}</div>
          ))}
          {query.trim() && matches.length === 0 && <div style={s.sub}>No unmatched results.</div>}
        </>
      )}
    </div>
  );

  return (
    <div style={{ marginTop: '1.5rem', padding: '14px 16px', background: '#fafafa', border: '1px solid #ececec', borderRadius: 8 }}>
      <h4 style={{ ...s.sectionTitle, fontSize: '1rem', margin: '0 0 4px' }}>Manually link a customer</h4>
      <p style={s.sub}>When the same customer is spelled very differently in SOS vs HubSpot, auto-match misses it. Find each side and link them into one account.</p>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {picker('SOS customer', 'Search SOS-only…', sosQ, setSosQ, sosMatches, sosPick, setSosPick, sosOnly.length)}
        {picker('HubSpot company', 'Search HubSpot-only…', hsQ, setHsQ, hsMatches, hsPick, setHsPick, hubspotOnly.length)}
      </div>
      <div style={{ marginTop: 10 }}>
        <button style={{ ...s.btnPrimary, opacity: (!sosPick || !hsPick || linking) ? 0.5 : 1 }} disabled={!sosPick || !hsPick || linking} onClick={link}>
          {linking ? 'Linking…' : 'Link these'}
        </button>
        {okMsg && <span style={{ ...s.sub, color: '#1a7f37', marginLeft: 12 }}>{okMsg}</span>}
        {err && <span style={{ ...s.errorText, marginLeft: 12 }}>{err}</span>}
      </div>
    </div>
  );
}

function CrosswalkTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    adminApi.getCrosswalk()
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function refresh() {
    setRefreshing(true);
    setError('');
    try {
      setData(await adminApi.refreshCrosswalk());
    } catch (e) { setError(e.message); }
    finally { setRefreshing(false); }
  }

  async function decide(action, sug) {
    setBusy(pairKey(sug.a, sug.b));
    setError('');
    try {
      const payload = { a: sug.a, b: sug.b };
      const res = action === 'confirm'
        ? await adminApi.confirmCrosswalkMatch(payload)
        : await adminApi.rejectCrosswalkMatch(payload);
      setData(res);
    } catch (e) { setError(e.message); }
    finally { setBusy(''); }
  }

  if (loading) return <div style={s.section}><p style={s.sub}>Loading crosswalk…</p></div>;

  const stats = data?.stats || {};
  const suggestions = data?.suggestions || [];
  const sosOnly = data?.unmatched?.sos || [];
  const hubspotOnly = data?.unmatched?.hubspot || [];
  const fetchedAt = data?.sourcesFetchedAt ? new Date(data.sourcesFetchedAt).toLocaleString('en-US', { timeZone: 'America/Denver' }) : '—';

  return (
    <div style={s.section}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <h3 style={{ ...s.sectionTitle, margin: 0 }}>Customer Crosswalk</h3>
        <button style={s.btnOutline} onClick={refresh} disabled={refreshing}>
          {refreshing ? 'Re-pulling SOS + HubSpot…' : 'Refresh from live data'}
        </button>
      </div>
      <p style={s.sub}>The unified customer master behind the sales tool. Live data last pulled: {fetchedAt}.</p>
      {error && <p style={s.errorText}>{error}</p>}

      <div style={s.kpiGrid}>
        <div style={s.kpiCard}><div style={s.kpiLabel}>Unified accounts</div><div style={s.kpiValue}>{stats.total ?? '—'}</div></div>
        <div style={s.kpiCard}><div style={s.kpiLabel}>Auto-linked</div><div style={s.kpiValue}>{stats.linked ?? '—'}</div></div>
        <div style={s.kpiCard}><div style={s.kpiLabel}>Confirmed by you</div><div style={s.kpiValue}>{stats.confirmed ?? 0}</div></div>
        <div style={s.kpiCard}><div style={s.kpiLabel}>SOS-only (active)</div><div style={s.kpiValue}>{stats.sosOnly ?? '—'}</div></div>
        <div style={s.kpiCard}><div style={s.kpiLabel}>Suggestions to review</div><div style={s.kpiValue}>{suggestions.length}</div></div>
      </div>

      <ManualLink sosOnly={sosOnly} hubspotOnly={hubspotOnly} onLinked={setData} />

      <h4 style={{ ...s.sectionTitle, fontSize: '1rem', marginTop: '1.5rem' }}>
        Suggested matches to confirm ({suggestions.length})
      </h4>
      <p style={s.sub}>Same customer, slightly different names across systems. Confirm to merge them into one account; reject to keep them separate.</p>
      {suggestions.length === 0 ? (
        <p style={s.sub}>Nothing waiting — every near-miss has been reviewed. 🎉</p>
      ) : (
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>One record…</th>
              <th style={s.th}>…likely the same as</th>
              <th style={s.thR}>Match</th>
              <th style={s.thR}>Action</th>
            </tr>
          </thead>
          <tbody>
            {suggestions.map((sug) => {
              const k = pairKey(sug.a, sug.b);
              const working = busy === k;
              return (
                <tr key={k}>
                  <td style={s.td}><strong>{sug.a.name}</strong><div style={s.customerMeta}>{SYSTEM_LABEL[sug.a.system] || sug.a.system}</div></td>
                  <td style={s.td}><strong>{sug.b.name}</strong><div style={s.customerMeta}>{SYSTEM_LABEL[sug.b.system] || sug.b.system}</div></td>
                  <td style={s.tdR}>{Math.round((sug.score || 0) * 100)}%</td>
                  <td style={s.tdR}>
                    <button style={{ ...s.btnSmall, marginRight: 6 }} disabled={working} onClick={() => decide('confirm', sug)}>
                      {working ? '…' : 'Confirm'}
                    </button>
                    <button style={s.btnOutline} disabled={working} onClick={() => decide('reject', sug)}>Reject</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <h4 style={{ ...s.sectionTitle, fontSize: '1rem', marginTop: '1.5rem' }}>
        Active SOS customers not yet matched ({sosOnly.length})
      </h4>
      <p style={s.sub}>
        These are live SOS customers with no exact match in HubSpot/Sage and no suggestion above.
        They're either spelled differently in HubSpot, or genuinely SOS-only. (Manual linking lands in the next update.)
      </p>
      {sosOnly.length > 0 && (
        <table style={s.table}>
          <thead><tr><th style={s.th}>Customer (SOS)</th></tr></thead>
          <tbody>
            {sosOnly.slice(0, 100).map((c) => (<tr key={c.id}><td style={s.td}>{c.name}</td></tr>))}
          </tbody>
        </table>
      )}
      {sosOnly.length > 100 && <p style={s.sub}>…and {sosOnly.length - 100} more.</p>}
    </div>
  );
}

// ── Export Tab ──
function ExportTab() {
  const [orderRange, setOrderRange] = useState('30d');
  const [custDays, setCustDays] = useState('90');
  const [downloading, setDownloading] = useState('');

  async function downloadCSV(type) {
    setDownloading(type);
    try {
      let csvText;
      let filename;
      if (type === 'orders') {
        const { start, end } = getDateRange(orderRange);
        csvText = await adminApi.exportOrders({ startdate: start, enddate: end });
        filename = `orders-export-${start}-to-${end}.csv`;
      } else if (type === 'contacts') {
        csvText = await adminApi.exportContacts();
        filename = `contacts-export-${new Date().toISOString().split('T')[0]}.csv`;
      } else {
        csvText = await adminApi.exportCustomers({ days: custDays });
        filename = `customers-export-${custDays}d.csv`;
      }
      // Trigger browser download
      const blob = new Blob([csvText], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed: ' + e.message);
    } finally {
      setDownloading('');
    }
  }

  return (
    <div>
      <div style={s.exportSection}>
        <h3 style={s.sectionTitle}>Export Orders</h3>
        <p style={s.sub}>Download order data as CSV (one row per line item).</p>
        <div style={{ ...s.rangeBar, marginBottom: '1rem' }}>
          {['7d', '30d', '90d', 'ytd'].map(r => (
            <button key={r} style={orderRange === r ? { ...s.rangeBtn, ...s.rangeBtnActive } : s.rangeBtn} onClick={() => setOrderRange(r)}>
              {r === 'ytd' ? 'YTD' : r}
            </button>
          ))}
        </div>
        <button style={s.btnPrimary} disabled={downloading === 'orders'} onClick={() => downloadCSV('orders')}>
          {downloading === 'orders' ? 'Downloading...' : 'Download Orders CSV'}
        </button>
      </div>

      <div style={{ ...s.exportSection, marginTop: '2rem' }}>
        <h3 style={s.sectionTitle}>Export Customers</h3>
        <p style={s.sub}>Download customer activity report as CSV.</p>
        <div style={{ ...s.rangeBar, marginBottom: '1rem' }}>
          {[['30', '30 days'], ['60', '60 days'], ['90', '90 days'], ['180', '6 months']].map(([val, label]) => (
            <button key={val} style={custDays === val ? { ...s.rangeBtn, ...s.rangeBtnActive } : s.rangeBtn} onClick={() => setCustDays(val)}>
              {label}
            </button>
          ))}
        </div>
        <button style={s.btnPrimary} disabled={downloading === 'customers'} onClick={() => downloadCSV('customers')}>
          {downloading === 'customers' ? 'Downloading...' : 'Download Customers CSV'}
        </button>
      </div>

      <div style={{ ...s.exportSection, marginTop: '2rem' }}>
        <h3 style={s.sectionTitle}>Export Contacts</h3>
        <p style={s.sub}>
          One row per portal contact (admin + employees) with the customer they belong to.
          Includes name, email, role, status, last login — the database for outreach.
        </p>
        <button style={s.btnPrimary} disabled={downloading === 'contacts'} onClick={() => downloadCSV('contacts')}>
          {downloading === 'contacts' ? 'Downloading...' : 'Download Contacts CSV'}
        </button>
      </div>
    </div>
  );
}

// ── New Customers Tab ──
// Reviews online new-customer applications and provisions them into SOS with one
// click. Each application shows the raw submission (left) plus a pre-filled,
// editable SOS customer draft (right). Staff verify/fix the draft, then "Create
// in SOS" — or search + link to an existing SOS customer to avoid duplicates.
// Nothing writes to HubSpot/QuickBooks yet (that's Phase 2).
const STATUS_STYLE = {
  new:         { background: 'rgba(230,160,30,0.14)', color: '#b3760b' },
  provisioned: { background: 'rgba(39,174,96,0.14)',  color: '#1e8449' },
  dismissed:   { background: 'rgba(120,120,120,0.14)', color: '#777' },
};

function setNested(obj, path, value) {
  const next = { ...obj };
  const keys = path.split('.');
  let cur = next;
  for (let i = 0; i < keys.length - 1; i++) {
    cur[keys[i]] = { ...(cur[keys[i]] || {}) };
    cur = cur[keys[i]];
  }
  cur[keys[keys.length - 1]] = value;
  return next;
}

function NewCustomersTab({ readOnly, canInvite }) {
  const [apps, setApps] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('new');
  const [expandedId, setExpandedId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState({});
  const [msg, setMsg] = useState({});
  const [linkTerm, setLinkTerm] = useState({});
  const [linkResults, setLinkResults] = useState({});
  // Invite management
  const [invites, setInvites] = useState([]);
  const [showInvites, setShowInvites] = useState(false);
  const [invName, setInvName] = useState('');
  const [invEmail, setInvEmail] = useState('');
  const [invSend, setInvSend] = useState(false);
  const [invBusy, setInvBusy] = useState(false);
  const [newLink, setNewLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [invMsg, setInvMsg] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await adminApi.listNewCustomers();
      setApps(res.applications || []);
    } catch (e) {
      setError(e.message);
    }
  }, []);
  const loadInvites = useCallback(async () => {
    try {
      const res = await adminApi.listNewCustomerInvites();
      setInvites(res.invites || []);
    } catch (e) { /* non-fatal */ }
  }, []);
  useEffect(() => { load(); loadInvites(); }, [load, loadInvites]);

  // Build the invite link for the CURRENT environment: prod points at the
  // newcustomer subdomain; staging/localhost reuse this origin + the hash route.
  function buildInviteLink(token) {
    const host = window.location.hostname;
    if (host.endsWith('silverservice.com')) return `https://newcustomer.silverservice.com/?invite=${token}`;
    return `${window.location.origin}/?invite=${token}#new-customer`;
  }

  async function generateInvite() {
    setInvBusy(true); setInvMsg(''); setNewLink(''); setCopied(false);
    try {
      const res = await adminApi.createNewCustomerInvite({
        prospectName: invName.trim(),
        prospectEmail: invEmail.trim(),
        sendEmail: invSend,
      });
      setNewLink(buildInviteLink(res.token));
      setInvMsg(invSend && res.emailed ? `Invite emailed to ${invEmail.trim()}.` : '');
      setInvName(''); setInvEmail(''); setInvSend(false);
      await loadInvites();
    } catch (e) {
      setInvMsg(e.message);
    } finally {
      setInvBusy(false);
    }
  }

  async function copyLink() {
    try { await navigator.clipboard.writeText(newLink); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* clipboard blocked */ }
  }

  async function revokeInvite(token) {
    if (!window.confirm('Revoke this invite link? Anyone holding it will no longer be able to open the form.')) return;
    try { await adminApi.revokeNewCustomerInvite(token); await loadInvites(); } catch (e) { setInvMsg(e.message); }
  }

  function toggleExpand(app) {
    const opening = expandedId !== app.id;
    setExpandedId(opening ? app.id : null);
    if (opening && !drafts[app.id]) {
      setDrafts(d => ({ ...d, [app.id]: app.sosDraft || {} }));
    }
  }
  const updateDraft = (id, path, value) =>
    setDrafts(d => ({ ...d, [id]: setNested(d[id] || {}, path, value) }));

  async function createInSos(app) {
    setBusy(b => ({ ...b, [app.id]: true }));
    setMsg(m => ({ ...m, [app.id]: null }));
    try {
      const res = await adminApi.provisionNewCustomerSos(app.id, { customer: drafts[app.id] });
      setMsg(m => ({ ...m, [app.id]: { type: 'ok', text: `Created in SOS${res.sosCustomerId ? ` (#${res.sosCustomerId})` : ''}. ${res.name || ''}` } }));
      await load();
    } catch (e) {
      setMsg(m => ({ ...m, [app.id]: { type: 'err', text: e.message } }));
    } finally {
      setBusy(b => ({ ...b, [app.id]: false }));
    }
  }

  async function linkExisting(app, sosCustomerId) {
    setBusy(b => ({ ...b, [app.id]: true }));
    setMsg(m => ({ ...m, [app.id]: null }));
    try {
      await adminApi.provisionNewCustomerSos(app.id, { sosCustomerId });
      setMsg(m => ({ ...m, [app.id]: { type: 'ok', text: `Linked to existing SOS customer #${sosCustomerId}.` } }));
      await load();
    } catch (e) {
      setMsg(m => ({ ...m, [app.id]: { type: 'err', text: e.message } }));
    } finally {
      setBusy(b => ({ ...b, [app.id]: false }));
    }
  }

  async function doDismiss(app) {
    if (!window.confirm('Dismiss this application? It will be marked handled and hidden from the default view.')) return;
    setBusy(b => ({ ...b, [app.id]: true }));
    try {
      await adminApi.dismissNewCustomer(app.id);
      await load();
    } catch (e) {
      setMsg(m => ({ ...m, [app.id]: { type: 'err', text: e.message } }));
    } finally {
      setBusy(b => ({ ...b, [app.id]: false }));
    }
  }

  async function downloadCert(app) {
    try {
      const res = await adminApi.newCustomerCertificateUrl(app.id);
      if (res.url) window.open(res.url, '_blank', 'noopener');
    } catch (e) {
      setMsg(m => ({ ...m, [app.id]: { type: 'err', text: e.message } }));
    }
  }

  async function searchExisting(app) {
    const term = (linkTerm[app.id] || '').trim();
    if (!term) return;
    try {
      const res = await adminApi.listCustomers({ search: term });
      setLinkResults(r => ({ ...r, [app.id]: res.customers || [] }));
    } catch (e) {
      setMsg(m => ({ ...m, [app.id]: { type: 'err', text: e.message } }));
    }
  }

  if (error) return <div style={s.errorText}>{error}</div>;
  if (!apps) return <div style={s.sub}>Loading applications…</div>;

  const visible = apps.filter(a => filter === 'all' ? true : (a.status || 'new') === 'new');
  const newCount = apps.filter(a => (a.status || 'new') === 'new').length;

  const activeInvites = invites.filter(i => i.valid).length;

  return (
    <div>
      {/* Invite panel — the form is invite-only; mint + manage links here */}
      {canInvite && (
        <div style={{ ...s.section, marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div style={s.sectionTitle}>Invite a customer to apply</div>
              <div style={s.customerMeta}>The application form is invite-only. Generate a private link and send it to a prospect.</div>
            </div>
            <button style={s.btnOutline} onClick={() => setShowInvites(v => !v)}>
              {showInvites ? 'Hide' : 'Manage'} invites ({activeInvites} active)
            </button>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: '1rem', alignItems: 'center' }}>
            <input style={{ ...s.input, flex: '1 1 180px' }} placeholder="Prospect name (optional)" value={invName} onChange={e => setInvName(e.target.value)} />
            <input style={{ ...s.input, flex: '1 1 200px' }} placeholder="Prospect email (optional)" value={invEmail} onChange={e => setInvEmail(e.target.value)} />
            <label style={{ ...s.customerMeta, display: 'flex', alignItems: 'center', gap: 6, cursor: invEmail.trim() ? 'pointer' : 'not-allowed', opacity: invEmail.trim() ? 1 : 0.5 }}>
              <input type="checkbox" disabled={!invEmail.trim()} checked={invSend} onChange={e => setInvSend(e.target.checked)} />
              Email it to them
            </label>
            <button style={s.btnPrimary} disabled={invBusy} onClick={generateInvite}>
              {invBusy ? 'Generating…' : 'Generate link'}
            </button>
          </div>

          {newLink && (
            <div style={{ display: 'flex', gap: 8, marginTop: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <input readOnly value={newLink} style={{ ...s.input, flex: 1, minWidth: 240, background: '#faf9f7' }} onFocus={e => e.target.select()} />
              <button style={s.btnPrimary} onClick={copyLink}>{copied ? '✓ Copied' : 'Copy link'}</button>
            </div>
          )}
          {invMsg && <div style={{ ...s.customerMeta, marginTop: 8, color: '#1e8449' }}>{invMsg}</div>}

          {showInvites && (
            <div style={{ marginTop: '1rem', borderTop: '1px solid #f0f0f0', paddingTop: '0.75rem' }}>
              {invites.length === 0 && <div style={s.customerMeta}>No invites yet.</div>}
              {invites.map(inv => (
                <div key={inv.token} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid #f7f7f7', gap: 8, flexWrap: 'wrap' }}>
                  <div>
                    <span style={{ fontWeight: 600, color: '#444', fontSize: '0.85rem' }}>{inv.prospectName || inv.prospectEmail || '(unnamed)'}</span>
                    <span style={{ ...s.customerMeta, marginLeft: 8 }}>
                      {inv.valid ? `active · expires ${fmtDate(inv.expiresAt)}` : (inv.status === 'revoked' ? 'revoked' : 'expired')}
                      {inv.usedCount ? ` · used ${inv.usedCount}×` : ''}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={s.btnOutline} onClick={() => { navigator.clipboard?.writeText(buildInviteLink(inv.token)); }}>Copy</button>
                    {inv.valid && <button style={s.btnOutline} onClick={() => revokeInvite(inv.token)}>Revoke</button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={s.filterBar}>
        <button style={filter === 'new' ? { ...s.rangeBtn, ...s.rangeBtnActive } : s.rangeBtn} onClick={() => setFilter('new')}>
          New ({newCount})
        </button>
        <button style={filter === 'all' ? { ...s.rangeBtn, ...s.rangeBtnActive } : s.rangeBtn} onClick={() => setFilter('all')}>
          All ({apps.length})
        </button>
        <button style={{ ...s.btnOutline, marginLeft: 'auto' }} onClick={load}>↻ Refresh</button>
      </div>

      {visible.length === 0 && (
        <div style={{ ...s.section, textAlign: 'center', color: '#888' }}>
          {filter === 'new' ? 'No new applications right now.' : 'No applications yet.'}
        </div>
      )}

      <div style={s.customerList}>
        {visible.map(app => {
          const status = app.status || 'new';
          const expanded = expandedId === app.id;
          const draft = drafts[app.id] || app.sosDraft || {};
          const m = msg[app.id];
          const isBusy = !!busy[app.id];
          return (
            <div key={app.id} style={s.customerCard}>
              <div style={s.customerRow} onClick={() => toggleExpand(app)}>
                <div style={s.customerInfo}>
                  <span style={s.customerName}>{app.legalBusinessName || app.dbaName || '(no name)'}</span>
                  <span style={s.customerMeta}>
                    {app.mainContactName} · {app.mainContactEmail} · {fmtDate(app.submittedAt)}
                  </span>
                </div>
                <div style={s.customerStats}>
                  {app.sosCustomerId && <span style={ns.sosId}>SOS #{app.sosCustomerId}</span>}
                  <span style={{ ...s.badge, ...(STATUS_STYLE[status] || {}) }}>{status}</span>
                  <span style={s.expandIcon}>{expanded ? '▲' : '▼'}</span>
                </div>
              </div>

              {expanded && (
                <div style={s.expandedContent}>
                  <div style={ns.grid}>
                    {/* Left — the raw submission */}
                    <div style={ns.col}>
                      <div style={ns.colTitle}>Application</div>
                      <Detail label="Legal name" value={app.legalBusinessName} />
                      <Detail label="DBA" value={app.dbaName} />
                      <Detail label="Business type" value={app.businessTypeOther ? `${app.businessType} — ${app.businessTypeOther}` : app.businessType} />
                      <Detail label="Billing" value={app.billingAddress} />
                      <Detail label="Shipping" value={app.sameAsBilling ? 'Same as billing' : app.shippingAddress} />
                      <Detail label="Main contact" value={`${app.mainContactName || ''} · ${app.mainContactPhone || ''}`} />
                      <Detail label="Email" value={app.mainContactEmail} />
                      <Detail label="A/P" value={[app.apContactName, app.apContactEmail].filter(Boolean).join(' · ')} />
                      <Detail label="Business phone" value={app.businessPhone} />
                      <Detail label="EIN" value={app.ein} />
                      <Detail label="Tax state / license" value={[app.taxState, app.salesTaxLicense].filter(Boolean).join(' · ')} />
                      <Detail label="Resale cert" value={app.hasCertificate ? 'On file' : 'Not provided'} />
                      {app.hasCertificate && (
                        <button style={{ ...s.btnOutline, alignSelf: 'flex-start' }} onClick={() => downloadCert(app)}>
                          ⬇ Download certificate (attach in SOS)
                        </button>
                      )}
                      <Detail label="Payment" value={app.paymentMethod} />
                      {app.paymentMethod === 'net' && <Detail label="Net days / bank" value={[app.netDays, app.bankReference].filter(Boolean).join(' · ')} />}
                      <Detail label="Signed" value={[app.acknowledgeName, app.acknowledgeTitle].filter(Boolean).join(' · ')} />
                    </div>

                    {/* Right — the SOS action */}
                    <div style={ns.col}>
                      {status === 'provisioned' ? (
                        <div style={{ ...s.section, margin: 0 }}>
                          <div style={ns.colTitle}>Provisioned ✓</div>
                          <p style={s.customerMeta}>
                            {app.sosLinked ? 'Linked to existing' : 'Created as new'} SOS customer
                            {app.sosCustomerId ? ` #${app.sosCustomerId}` : ''}
                            {app.sosTest ? ' (TEST — staging)' : ''}.
                            {app.provisionedBy ? ` By ${app.provisionedBy}.` : ''}
                          </p>
                        </div>
                      ) : status === 'dismissed' ? (
                        <div style={{ ...s.section, margin: 0, color: '#888' }}>Dismissed.</div>
                      ) : readOnly ? (
                        <div style={s.customerMeta}>View only — the office turns applications into SOS customers.</div>
                      ) : (
                        <>
                          <div style={ns.colTitle}>Create in SOS</div>
                          <Row2>
                            <DraftField label="Customer name" v={draft.name} on={v => updateDraft(app.id, 'name', v)} />
                            <DraftField label="Company name" v={draft.companyName} on={v => updateDraft(app.id, 'companyName', v)} />
                          </Row2>
                          <Row2>
                            <DraftField label="Email" v={draft.email} on={v => updateDraft(app.id, 'email', v)} />
                            <DraftField label="Phone" v={draft.phone} on={v => updateDraft(app.id, 'phone', v)} />
                          </Row2>
                          <Row2>
                            <DraftField label="Contact first name" v={draft.contact?.firstName} on={v => updateDraft(app.id, 'contact.firstName', v)} />
                            <DraftField label="Contact last name" v={draft.contact?.lastName} on={v => updateDraft(app.id, 'contact.lastName', v)} />
                          </Row2>
                          <AddrFields id={app.id} which="billing" label="Billing address" draft={draft} on={updateDraft} />
                          <AddrFields id={app.id} which="shipping" label="Shipping address" draft={draft} on={updateDraft} />
                          <Row2>
                            <DraftField label="Resale number" v={draft.resaleNumber} on={v => updateDraft(app.id, 'resaleNumber', v)} />
                            <label style={{ ...ns.fieldWrap, justifyContent: 'flex-end', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                              <input type="checkbox" checked={draft.taxable !== false} onChange={e => updateDraft(app.id, 'taxable', e.target.checked)} />
                              <span style={ns.fieldLabel}>Charge sales tax (uncheck for verified resale/exempt)</span>
                            </label>
                          </Row2>
                          <DraftField label="Notes" v={draft.notes} on={v => updateDraft(app.id, 'notes', v)} textarea />

                          <div style={ns.actions}>
                            <button style={s.btnPrimary} disabled={isBusy} onClick={() => createInSos(app)}>
                              {isBusy ? 'Working…' : 'Create in SOS'}
                            </button>
                            <button style={s.btnOutline} disabled={isBusy} onClick={() => doDismiss(app)}>Dismiss</button>
                          </div>

                          {/* Link to existing (dedupe) */}
                          <div style={ns.linkBox}>
                            <div style={ns.colTitle}>Already in SOS? Search &amp; link instead</div>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <input
                                style={{ ...s.input, flex: 1 }}
                                placeholder="Search SOS customers by name"
                                value={linkTerm[app.id] || ''}
                                onChange={e => setLinkTerm(t => ({ ...t, [app.id]: e.target.value }))}
                                onKeyDown={e => e.key === 'Enter' && searchExisting(app)}
                              />
                              <button style={s.btnOutline} onClick={() => searchExisting(app)}>Search</button>
                            </div>
                            {(linkResults[app.id] || []).map(c => (
                              <div key={c.id} style={ns.linkResult}>
                                <span>{c.name} <span style={s.customerMeta}>#{c.id}{c.email ? ` · ${c.email}` : ''}</span></span>
                                <button style={s.btnOutline} disabled={isBusy} onClick={() => linkExisting(app, c.id)}>Link</button>
                              </div>
                            ))}
                          </div>
                        </>
                      )}

                      {m && <div style={m.type === 'ok' ? ns.okMsg : ns.errMsg}>{m.text}</div>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  if (!value) return null;
  return (
    <div style={ns.detail}>
      <span style={ns.detailLabel}>{label}</span>
      <span style={ns.detailValue}>{value}</span>
    </div>
  );
}
function DraftField({ label, v, on, textarea }) {
  return (
    <label style={ns.fieldWrap}>
      <span style={ns.fieldLabel}>{label}</span>
      {textarea
        ? <textarea style={{ ...s.input, minHeight: 60, resize: 'vertical' }} value={v || ''} onChange={e => on(e.target.value)} />
        : <input style={s.input} value={v || ''} onChange={e => on(e.target.value)} />}
    </label>
  );
}
function Row2({ children }) {
  return <div style={ns.row2}>{children}</div>;
}
function AddrFields({ id, which, label, draft, on }) {
  const a = draft[which] || {};
  return (
    <div style={ns.addrBlock}>
      <div style={ns.fieldLabel}>{label}</div>
      <DraftField label="Line 1" v={a.line1} on={v => on(id, `${which}.line1`, v)} />
      <Row2>
        <DraftField label="City" v={a.city} on={v => on(id, `${which}.city`, v)} />
        <DraftField label="State" v={a.stateProvince} on={v => on(id, `${which}.stateProvince`, v)} />
      </Row2>
      <Row2>
        <DraftField label="ZIP" v={a.postalCode} on={v => on(id, `${which}.postalCode`, v)} />
        <DraftField label="Country" v={a.country} on={v => on(id, `${which}.country`, v)} />
      </Row2>
    </div>
  );
}

const ns = {
  grid: { display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: '1.5rem', paddingTop: '1rem' },
  col: { display: 'flex', flexDirection: 'column', gap: '0.4rem', minWidth: 0 },
  colTitle: { fontSize: '0.8rem', fontWeight: 700, color: '#333', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 },
  detail: { display: 'flex', flexDirection: 'column', paddingBottom: 6, borderBottom: '1px solid #f5f5f5' },
  detailLabel: { fontSize: '0.68rem', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.04em' },
  detailValue: { fontSize: '0.85rem', color: '#444', whiteSpace: 'pre-wrap' },
  fieldWrap: { display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 0 },
  fieldLabel: { fontSize: '0.72rem', color: '#888', fontWeight: 600 },
  row2: { display: 'flex', gap: '0.6rem' },
  addrBlock: { display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.6rem', background: '#faf9f7', borderRadius: 8 },
  actions: { display: 'flex', gap: '0.6rem', marginTop: '0.75rem' },
  linkBox: { marginTop: '1rem', paddingTop: '1rem', borderTop: '1px dashed #e0e0e0', display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  linkResult: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 0', fontSize: '0.85rem', color: '#444' },
  sosId: { fontSize: '0.72rem', color: '#1e8449', fontWeight: 600 },
  okMsg: { marginTop: '0.75rem', fontSize: '0.85rem', color: '#1e8449', background: 'rgba(39,174,96,0.08)', borderRadius: 6, padding: '0.6rem 0.8rem' },
  errMsg: { marginTop: '0.75rem', fontSize: '0.85rem', color: '#c0392b', background: 'rgba(231,76,60,0.08)', borderRadius: 6, padding: '0.6rem 0.8rem' },
};

// ── Settings: integrations ──────────────────────────────────────────────────
// QuickBooks tokens expire on Intuit's schedule, not ours, and when they do
// every customer's Invoices tab goes dark. This turns recovery into one click
// instead of a trip through Intuit's OAuth playground.
function SettingsTab() {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    setError('');
    adminApi.qboStatus()
      .then(setStatus)
      .catch(e => setError(e.message));
  };
  useEffect(load, []);

  // Intuit sends the admin back here with ?qbo=<outcome> after the consent screen.
  const outcome = new URLSearchParams(window.location.search).get('qbo');

  const connect = () => {
    setBusy(true); setError('');
    adminApi.qboAuthorizeUrl()
      .then(({ url }) => { window.location.href = url; })
      .catch(e => { setError(e.message); setBusy(false); });
  };

  const connected = status?.healthy;
  return (
    <div>
      <h2 style={s.h2}>Settings</h2>

      {outcome === 'connected' && <div style={qs_.ok}>QuickBooks reconnected. Invoices are live again.</div>}
      {outcome === 'denied' && <div style={qs_.warn}>Connection cancelled at QuickBooks — nothing changed.</div>}
      {(outcome === 'failed' || outcome === 'nocode') && <div style={qs_.warn}>QuickBooks did not complete the connection. Try again.</div>}
      {outcome === 'badstate' && <div style={qs_.warn}>That connection link expired. Start again from this page.</div>}

      <div style={qs_.card}>
        <div style={qs_.cardHead}>
          <div>
            <div style={qs_.title}>QuickBooks Online</div>
            <div style={qs_.sub}>Source of customer invoices, credit memos, and balances.</div>
          </div>
          <span style={{ ...qs_.pill, ...(connected ? qs_.pillOk : qs_.pillBad) }}>
            {status ? (connected ? 'Connected' : 'Action needed') : 'Checking…'}
          </span>
        </div>

        {status && (
          <div style={qs_.rows}>
            <div><span style={qs_.k}>Company (realm)</span><span style={qs_.v}>{status.realmId || '—'}</span></div>
            <div><span style={qs_.k}>Environment</span><span style={qs_.v}>{status.environment}</span></div>
            <div><span style={qs_.k}>Last connected</span><span style={qs_.v}>{status.connectedAt ? new Date(status.connectedAt).toLocaleString() : '—'}</span></div>
          </div>
        )}

        {status && !connected && status.problem && <div style={qs_.warn}>{status.problem}</div>}
        {error && <div style={qs_.warn}>{error}</div>}

        <div style={qs_.actions}>
          <button style={s.btn} onClick={connect} disabled={busy}>
            {busy ? 'Opening QuickBooks…' : connected ? 'Reconnect QuickBooks' : 'Connect QuickBooks'}
          </button>
          <button style={s.btnOutline} onClick={load} disabled={busy}>Re-check</button>
        </div>

        {status?.redirectUri && (
          <div style={qs_.hint}>
            Intuit app redirect URI (must match exactly in the Intuit developer dashboard):
            <code style={qs_.code}>{status.redirectUri}</code>
          </div>
        )}
      </div>
    </div>
  );
}

const qs_ = {
  card: { background: '#fff', border: '1px solid #e5e5e5', borderRadius: 10, padding: '1.25rem 1.4rem', maxWidth: 640 },
  cardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' },
  title: { fontWeight: 700, fontSize: '1.05rem' },
  sub: { fontSize: '0.85rem', color: '#666', marginTop: '0.15rem' },
  pill: { fontSize: '0.75rem', fontWeight: 700, padding: '0.3rem 0.7rem', borderRadius: 999, whiteSpace: 'nowrap' },
  pillOk: { background: 'rgba(39,174,96,0.12)', color: '#1e8449' },
  pillBad: { background: 'rgba(231,76,60,0.10)', color: '#c0392b' },
  rows: { display: 'grid', gap: '0.4rem', fontSize: '0.88rem', marginBottom: '0.9rem' },
  k: { color: '#777', display: 'inline-block', width: 170 },
  v: { fontWeight: 600 },
  ok: { background: 'rgba(39,174,96,0.10)', color: '#1e8449', borderRadius: 8, padding: '0.7rem 0.9rem', marginBottom: '1rem', fontSize: '0.9rem' },
  warn: { background: 'rgba(231,76,60,0.08)', color: '#c0392b', borderRadius: 8, padding: '0.7rem 0.9rem', margin: '0.5rem 0', fontSize: '0.9rem' },
  actions: { display: 'flex', gap: '0.6rem', marginTop: '0.4rem' },
  hint: { marginTop: '1rem', fontSize: '0.78rem', color: '#777', lineHeight: 1.6 },
  code: { display: 'block', marginTop: '0.3rem', background: '#f6f6f6', padding: '0.4rem 0.6rem', borderRadius: 6, wordBreak: 'break-all', color: '#333' },
};

// ── Styles ──
const s = {
  // Layout
  page: { minHeight: '100vh', background: '#f5f5f5', display: 'flex', justifyContent: 'center', padding: '2rem 1rem', fontFamily: "'Inter', sans-serif" },
  container: { width: '100%', maxWidth: 1100 },

  // Top bar
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' },
  pageTitle: { fontSize: '1.5rem', fontWeight: 700, color: '#333', margin: '0 0 4px' },
  sub: { color: '#888', fontSize: '0.85rem', margin: 0 },

  // Nav: a row of group buttons, then the open group's tabs beneath it.
  groupBar: { display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' },
  groupBtn: { padding: '0.55rem 1.1rem', borderRadius: 8, border: '1px solid #ddd', background: '#fff', fontSize: '0.88rem', fontWeight: 600, color: '#555', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s' },
  groupBtnActive: { background: '#A50B18', border: '1px solid #A50B18', color: '#fff' },
  tabs: { display: 'flex', flexWrap: 'wrap', gap: 0, marginBottom: '1.5rem', borderBottom: '2px solid #e0e0e0' },
  tab: { padding: '0.6rem 0.85rem', border: 'none', background: 'none', fontSize: '0.85rem', fontWeight: 500, color: '#888', cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: -2, whiteSpace: 'nowrap', transition: 'all 0.15s' },
  tabActive: { color: '#A50B18', borderBottom: '2px solid #A50B18', fontWeight: 600 },

  // Inputs & Buttons
  input: { padding: '0.7rem 1rem', borderRadius: 8, border: '1px solid #ddd', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' },
  btnPrimary: { background: '#A50B18', color: '#fff', border: 'none', borderRadius: 8, padding: '0.75rem 1.5rem', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' },
  btnOutline: { background: 'transparent', color: '#888', border: '1px solid #ddd', borderRadius: 8, padding: '0.5rem 1rem', fontSize: '0.82rem', cursor: 'pointer' },
  btnSmall: { background: '#A50B18', color: '#fff', border: 'none', borderRadius: 6, padding: '0.4rem 1rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' },

  // Range selector
  rangeBar: { display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' },
  rangeBtn: { padding: '0.4rem 0.9rem', borderRadius: 6, border: '1px solid #ddd', background: '#fff', fontSize: '0.8rem', cursor: 'pointer', color: '#555', fontWeight: 500 },
  rangeBtnActive: { background: '#A50B18', color: '#fff', border: '1px solid #A50B18' },
  totalLabel: { marginLeft: 'auto', fontSize: '0.85rem', color: '#888', fontWeight: 500 },

  // KPI cards
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' },
  kpiCard: { background: '#fff', borderRadius: 10, padding: '1.25rem 1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  kpiLabel: { fontSize: '0.78rem', color: '#888', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.5rem' },
  kpiValue: { fontSize: '1.6rem', fontWeight: 700, color: '#333' },

  // Sections
  section: { background: '#fff', borderRadius: 10, padding: '1.25rem 1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '1rem' },
  sectionTitle: { fontSize: '0.95rem', fontWeight: 600, color: '#333', margin: '0 0 1rem' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' },

  // Chart
  chartContainer: { display: 'flex', alignItems: 'flex-end', gap: 2, height: 120, padding: '0 0 1.5rem' },
  chartCol: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0 },
  chartBarWrap: { width: '100%', height: 100, display: 'flex', alignItems: 'flex-end' },
  chartBar: { width: '100%', background: '#A50B18', borderRadius: '3px 3px 0 0', minHeight: 2, transition: 'height 0.3s' },
  chartLabel: { fontSize: '0.6rem', color: '#aaa', marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden' },

  // Table
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' },
  th: { textAlign: 'left', padding: '0.6rem 0.75rem', borderBottom: '1px solid #eee', fontSize: '0.75rem', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' },
  thR: { textAlign: 'right', padding: '0.6rem 0.75rem', borderBottom: '1px solid #eee', fontSize: '0.75rem', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' },
  td: { padding: '0.6rem 0.75rem', borderBottom: '1px solid #f5f5f5', color: '#444' },
  tdR: { padding: '0.6rem 0.75rem', borderBottom: '1px solid #f5f5f5', color: '#444', textAlign: 'right' },

  // Pagination
  pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1rem' },
  pageBtn: { padding: '0.5rem 1rem', borderRadius: 6, border: '1px solid #ddd', background: '#fff', fontSize: '0.82rem', cursor: 'pointer', color: '#555' },
  pageInfo: { fontSize: '0.85rem', color: '#888' },

  // Customer list
  customerList: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  customerCard: { background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' },
  customerRow: { padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', gap: '1rem', flexWrap: 'wrap' },
  customerInfo: { display: 'flex', flexDirection: 'column', gap: 2 },
  customerName: { fontWeight: 600, color: '#333', fontSize: '0.95rem' },
  customerMeta: { fontSize: '0.8rem', color: '#888' },
  customerStats: { display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' },
  badge: { fontSize: '0.72rem', padding: '2px 10px', borderRadius: 12, fontWeight: 600 },
  statText: { fontSize: '0.82rem', color: '#666' },
  expandIcon: { color: '#aaa', fontSize: '0.8rem' },
  expandedContent: { padding: '0 1.25rem 1rem', borderTop: '1px solid #f0f0f0' },
  expandedRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0', gap: '1rem', flexWrap: 'wrap', fontSize: '0.85rem', color: '#555' },
  inviteActions: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  filterBar: { display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' },

  // Export
  exportSection: { background: '#fff', borderRadius: 10, padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },

  // Error
  errorText: { color: '#e74c3c', fontSize: '0.9rem' },

  // Admin login + identity
  loginError: { color: '#c0392b', fontSize: '0.85rem', background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.2)', borderRadius: 6, padding: '0.6rem 0.8rem', marginTop: '-0.25rem' },
  topBarRight: { display: 'flex', gap: '0.75rem', alignItems: 'center' },
  userChip: { display: 'flex', gap: '0.6rem', alignItems: 'center', padding: '0.35rem 0.75rem', background: '#fff', border: '1px solid #e0e0e0', borderRadius: 24 },
  avatar: { width: 32, height: 32, borderRadius: '50%', background: '#A50B18', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, flexShrink: 0 },
  userChipText: { display: 'flex', flexDirection: 'column' },
  userChipName: { fontSize: '0.85rem', fontWeight: 600, color: '#333', lineHeight: 1.2 },
  userChipRole: { fontSize: '0.7rem', color: 'rgba(0,0,0,0.45)', letterSpacing: '0.05em', textTransform: 'uppercase' },

  // Representatives tab
  repsHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem', flexWrap: 'wrap' },
  repsTitleRow: { display: 'flex', gap: '0.6rem', alignItems: 'center' },
  repsTitle: { fontSize: '1.25rem', fontWeight: 600, color: '#333', margin: 0 },
  repsCount: { fontSize: '0.85rem', color: 'rgba(0,0,0,0.55)', background: '#f0eeec', padding: '0.2rem 0.65rem', borderRadius: 12 },
  repsSearch: { flex: 1, maxWidth: 380, padding: '0.6rem 0.9rem', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.9rem', outline: 'none' },
  repsTable: { background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' },
  repsTableHead: { display: 'flex', padding: '0.85rem 1.25rem', borderBottom: '1px solid #eee', fontSize: '0.72rem', fontWeight: 600, color: 'rgba(0,0,0,0.45)', letterSpacing: '0.08em', textTransform: 'uppercase' },
  repsRow: { display: 'flex', padding: '0.9rem 1.25rem', borderBottom: '1px solid #f2f0ee', alignItems: 'center' },
  repsCell: { fontSize: '0.9rem', color: '#333' },
  repsAvatar: { width: 36, height: 36, borderRadius: '50%', background: '#7a8b4f', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.82rem', fontWeight: 700 },
  repsEmpty: { padding: '2rem', textAlign: 'center', color: 'rgba(0,0,0,0.4)', fontSize: '0.9rem' },
  statusActive: { fontSize: '0.78rem', color: '#2e7d32', background: 'rgba(46,125,50,0.1)', padding: '0.2rem 0.55rem', borderRadius: 12, fontWeight: 500 },
  statusInvited: { fontSize: '0.78rem', color: '#b26500', background: 'rgba(178,101,0,0.1)', padding: '0.2rem 0.55rem', borderRadius: 12, fontWeight: 500 },
  statusRemoved: { fontSize: '0.78rem', color: 'rgba(0,0,0,0.5)', background: 'rgba(0,0,0,0.07)', padding: '0.2rem 0.55rem', borderRadius: 12, fontWeight: 500 },
  repsRowRemoved: { background: '#faf9f7', color: 'rgba(0,0,0,0.5)' },
  repsRemovedToggle: { background: 'none', border: 'none', color: '#A50B18', fontSize: '0.8rem', cursor: 'pointer', padding: 0, fontFamily: 'inherit', textDecoration: 'underline' },
  repsRemoveBtn: { background: '#fff', border: '1px solid #e0c3c6', color: '#A50B18', borderRadius: 6, padding: '0.3rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 },
  repsRestoreBtn: { background: '#fff', border: '1px solid #c3d3bd', color: '#2e7d32', borderRadius: 6, padding: '0.3rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 },
  repsNoAction: { color: 'rgba(0,0,0,0.3)' },
  repsAddBtn: { background: '#A50B18', color: '#fff', border: 'none', borderRadius: 6, padding: '0.6rem 1rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  repsAddPanel: { background: '#fff', border: '1px solid #e8e3dc', borderRadius: 10, padding: '1rem 1.1rem', marginBottom: '1.25rem' },
  repsAddTitle: { fontWeight: 600, color: '#333', marginBottom: '0.75rem' },
  repsAddFields: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '0.75rem' },
  repsAddField: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
  repsAddLabel: { fontSize: '0.75rem', fontWeight: 600, color: '#666' },
  repsAddInput: { padding: '0.55rem 0.7rem', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.9rem', background: '#fff', fontFamily: 'inherit', minWidth: 0 },
  repsAddHint: { fontSize: '0.82rem', color: '#666', marginTop: '0.6rem' },
  repsAddActions: { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.9rem' },
  repsAddCancel: { background: 'none', border: '1px solid #ddd', color: '#444', borderRadius: 6, padding: '0.55rem 1rem', fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit' },
  repsAddSubmit: { background: '#A50B18', color: '#fff', border: 'none', borderRadius: 6, padding: '0.55rem 1.2rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  repsInviteBtn: { background: '#fff', border: '1px solid #d8d3cc', color: '#444', borderRadius: 6, padding: '0.3rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 },
  inviteModal: { background: '#fff', borderRadius: 12, padding: '1.5rem', maxWidth: 560, width: '100%', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  inviteUrlBox: { width: '100%', boxSizing: 'border-box', fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.5, padding: '0.65rem', border: '1px solid #ddd', borderRadius: 6, background: '#faf9f7', color: '#333', resize: 'vertical', minHeight: 84 },

  // Order Guide editor
  ogeHeader: { display: 'flex', alignItems: 'center', gap: '1.25rem', marginBottom: '1.25rem', flexWrap: 'wrap' },
  ogeBack: { background: 'none', border: 'none', color: '#A50B18', fontSize: '0.85rem', cursor: 'pointer', padding: '0.35rem 0', fontWeight: 500 },
  ogeTitleBlock: { flex: 1, minWidth: 220 },
  ogeTitle: { fontSize: '1.35rem', fontWeight: 600, color: '#333', margin: 0 },
  ogeSubtitle: { fontSize: '0.85rem', color: 'rgba(0,0,0,0.55)', marginTop: 2, display: 'flex', gap: '0.35rem', alignItems: 'center' },
  ogeDirty: { color: '#b26500', marginLeft: '0.4rem', fontWeight: 500 },
  ogeGrid: { display: 'grid', gridTemplateColumns: 'minmax(320px, 1fr) minmax(400px, 1.4fr)', gap: '1.25rem', alignItems: 'flex-start' },
  ogePanel: { background: '#fff', borderRadius: 10, padding: '1rem 1.1rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', minHeight: 400 },
  ogePanelHead: { display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.85rem' },
  ogePanelTitle: { fontSize: '1rem', fontWeight: 600, color: '#333', margin: 0 },
  ogeEmpty: { padding: '2rem', textAlign: 'center', color: 'rgba(0,0,0,0.4)', fontSize: '0.88rem', lineHeight: 1.5, background: '#faf9f7', borderRadius: 8 },
  ogeItemsList: { display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '70vh', overflow: 'auto' },
  ogeItem: { display: 'flex', gap: '0.75rem', alignItems: 'center', padding: '0.6rem 0.75rem', background: '#faf9f7', border: '1px solid #efedea', borderRadius: 8 },
  ogeItemHistory: { background: '#fff', border: '1px dashed #e3e0dc' },
  ogeHistoryTag: { fontSize: '0.72rem', color: '#7a6a3c', background: 'rgba(196,164,78,0.15)', padding: '0.15rem 0.5rem', borderRadius: 10, letterSpacing: '0.02em', fontWeight: 500 },
  ogeItemText: { flex: 1, minWidth: 0 },
  ogeItemName: { fontSize: '0.88rem', fontWeight: 500, color: '#333', lineHeight: 1.3 },
  ogeItemMeta: { display: 'flex', gap: '0.5rem', marginTop: 2, fontSize: '0.74rem', color: 'rgba(0,0,0,0.55)' },
  ogeSku: { fontFamily: 'monospace', color: 'rgba(0,0,0,0.45)' },
  ogeCat: { color: 'rgba(0,0,0,0.5)' },
  ogePrice: { color: '#A50B18', fontFamily: 'monospace', fontWeight: 500 },
  ogeItemControls: { display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0 },
  ogeParLabel: { display: 'flex', flexDirection: 'column', fontSize: '0.65rem', color: 'rgba(0,0,0,0.5)', letterSpacing: '0.04em', textTransform: 'uppercase', gap: 2 },
  ogeParInput: { width: 52, padding: '0.25rem 0.35rem', border: '1px solid #ddd', borderRadius: 4, fontSize: '0.85rem', textAlign: 'center', fontFamily: 'monospace' },
  ogeRemovedBlock: { marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid #ece8e3' },
  ogeRemovedHead: { fontSize: '0.8rem', fontWeight: 600, color: 'rgba(0,0,0,0.55)', textTransform: 'uppercase', letterSpacing: '0.04em' },
  ogeRemovedNote: { fontSize: '0.78rem', color: 'rgba(0,0,0,0.45)', margin: '0.2rem 0 0.5rem' },
  ogeRemovedItem: { display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.75rem', borderRadius: 8, background: '#faf9f7', marginBottom: '0.35rem' },
  ogeRemovedName: { fontSize: '0.88rem', color: 'rgba(0,0,0,0.5)', textDecoration: 'line-through' },
  ogeRemoveBtn: { background: 'none', border: 'none', color: '#c7c4c0', fontSize: '1.2rem', cursor: 'pointer', padding: '0 0.4rem', lineHeight: 1 },
  ogeCatalogList: { display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '70vh', overflow: 'auto' },
  ogeCatalogRow: { display: 'flex', gap: '0.75rem', alignItems: 'center', padding: '0.6rem 0.75rem', background: '#fff', border: '1px solid #efedea', borderRadius: 8 },
  ogeAddBtn: { background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 4, padding: '0.35rem 0.85rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0 },
  ogeAddedBadge: { color: '#2e7d32', fontSize: '0.8rem', fontWeight: 500, padding: '0.35rem 0.5rem', flexShrink: 0 },

  // Catalog (visibility toggles) tab
  catalogHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' },
  catalogHeaderRight: { display: 'flex', gap: '1rem', alignItems: 'center', flex: 1, justifyContent: 'flex-end', minWidth: 300 },
  catalogHeaderNote: { fontSize: '0.82rem', color: 'rgba(0,0,0,0.55)', whiteSpace: 'nowrap' },
  syncBtn: { background: '#A50B18', color: '#fff', border: 'none', borderRadius: 6, padding: '0.55rem 1rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: '0.02em' },
  syncBtnBusy: { background: 'rgba(165,11,24,0.6)', cursor: 'wait' },
  catEditBtn: { background: 'none', border: '1px solid transparent', borderRadius: 4, color: 'rgba(0,0,0,0.4)', cursor: 'pointer', padding: '1px 5px', fontSize: '0.78rem', lineHeight: 1, fontFamily: 'inherit' },
  modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' },
  catEditModal: { background: '#fff', borderRadius: 10, padding: '1.5rem', width: '100%', maxWidth: 480, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  catEditField: { display: 'block' },
  catEditLabel: { display: 'block', fontSize: '0.78rem', color: 'rgba(0,0,0,0.6)', marginBottom: '0.3rem', fontWeight: 600 },
  catEditInput: { width: '100%', padding: '0.55rem 0.7rem', border: '1px solid #ddd', borderRadius: 5, fontSize: '0.9rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' },
  catEditHint: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.6rem', fontSize: '0.75rem', color: 'rgba(0,0,0,0.5)', marginTop: '0.35rem' },
  catEditResetLink: { background: 'none', border: 'none', color: '#A50B18', fontSize: '0.75rem', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontFamily: 'inherit' },
  catalogStatusRow: { display: 'flex', gap: '0.4rem', marginBottom: '0.5rem', alignItems: 'center', flexWrap: 'wrap' },
  statusBtn: { background: '#fff', border: '1px solid #ddd', color: 'rgba(0,0,0,0.55)', borderRadius: 16, padding: '0.35rem 0.85rem', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500, transition: 'all 0.15s' },
  statusBtnActive: { fontWeight: 700, background: '#fff' },
  syncBannerOk: { fontSize: '0.85rem', color: '#1b4332', background: 'rgba(46,125,50,0.08)', border: '1px solid rgba(46,125,50,0.2)', borderRadius: 6, padding: '0.6rem 0.9rem', marginBottom: '0.75rem' },
  syncDriftList: { margin: '0.4rem 0 0', paddingLeft: '1.1rem', fontSize: '0.8rem', lineHeight: 1.5 },
  syncBannerErr: { fontSize: '0.85rem', color: '#c0392b', background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.2)', borderRadius: 6, padding: '0.6rem 0.9rem', marginBottom: '0.75rem' },
  catalogFilterRow: { display: 'flex', gap: '0.4rem', marginBottom: '1rem', flexWrap: 'wrap' },
  catalogThumb: { width: 56, height: 56, objectFit: 'contain', background: '#f7f5f2', borderRadius: 4, border: '1px solid #eee' },
  catalogThumbEmpty: { width: 56, height: 56, background: '#f7f5f2', borderRadius: 4, border: '1px dashed #ddd', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(0,0,0,0.4)', fontSize: '1.2rem' },
  catalogThumbBtn: { background: 'none', border: 'none', padding: 0, cursor: 'pointer', borderRadius: 4 },

  // Orders dashboard
  sosLinkBtn: { background: '#fff', border: '1px solid #A50B18', color: '#A50B18', borderRadius: 6, padding: '0.45rem 0.9rem', fontSize: '0.82rem', cursor: 'pointer', textDecoration: 'none', fontWeight: 600 },
  orderNumBtn: { background: 'none', border: 'none', padding: 0, color: '#A50B18', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', fontFamily: 'inherit' },
  copyToast: { position: 'fixed', bottom: '2rem', right: '2rem', background: '#333', color: '#fff', padding: '0.6rem 1rem', borderRadius: 8, fontSize: '0.85rem', zIndex: 1000, boxShadow: '0 4px 12px rgba(0,0,0,0.25)' },

  // Replenishment-specific
  fieldLabel: { fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 },
  smallInput: { padding: '0.35rem 0.6rem', borderRadius: 6, border: '1px solid #ddd', fontSize: '0.82rem', outline: 'none', width: 80, boxSizing: 'border-box' },
  linkChip: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 10px', background: '#fff', border: '1px solid #A50B18', borderRadius: 6, fontSize: '0.9rem' },
  linkChipX: { background: 'none', border: 'none', color: '#A50B18', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1 },
  linkRow: { padding: '7px 10px', background: '#fff', border: '1px solid #eee', borderTop: 'none', fontSize: '0.86rem', cursor: 'pointer' },
};

// Pointer for the screens that moved off Manage on 2026-10-18, so nobody goes
// looking for them. Delete once people have the new homes in their fingers.
function MovedNotice({ role }) {
  const sales = siteUrl(siteByKey('sales'));
  const reports = siteUrl(siteByKey('reports'));
  const link = { color: '#A50B18', fontWeight: 600 };
  if (role !== 'owner' && role !== 'sales_rep' && role !== 'silver_service_admin') return null;
  return (
    <div style={{ background: '#fff8e6', border: '1px solid #f3dca5', borderRadius: 8, padding: '0.55rem 0.9rem', fontSize: '0.85rem', marginBottom: '1rem' }}>
      Moved: <strong>Win-back</strong> and each account's AI summary are in <a href={sales} style={link}>Sales</a>
      {role === 'owner' && <> · the <strong>Dashboard</strong> and <strong>Owner Metrics</strong> are in <a href={reports} style={link}>Reports</a></>}.
    </div>
  );
}
